import { Repayment, Repayments } from '@/domain/repayments'
import { isTerminalRequired } from '@/domain/repayment-validation'
import { Injectable, OnDestroy } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { BehaviorSubject, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { APP_CONFIG } from '../config/environment'
import { AuthService } from './auth.service'
import { QueryParamsService } from './query-params.service'
import { RepaymentsTableQueryService } from './repayments-table-query.service'

interface RequestOptions {
  signal?: AbortSignal
  /** Custom uploads do not provide a merchant for every repayment. */
  requireMerchant?: boolean
  /** Called once the server has durably queued the asynchronous commit job. */
  onQueued?: () => void
  onProgress?: (completed: number, total: number) => void
}

export interface FileRepaymentValidationResult {
  valid: boolean
  errors: string[]
}

export interface RepaymentDailyAggregate {
  repayment_date?: string
  repayment_month?: string
  created_date?: string
  data_source?: string
  repayment_sum?: number | string
  repayment_count?: number | string
}

export interface OperationsRepaymentPage {
  repayments: Repayments
  totalRecords?: number
  totalPaymentAmount?: number
}

export interface LegalInternalRecoveryContribution {
  repayment_date?: string
  created_date?: string
  payment_sum?: number | string
  merchants?: { name?: string } | Array<{ name?: string }>
  cash_advances?: { name?: string } | Array<{ name?: string }>
}

export interface AllSalesRepayment {
  id?: string
  merchant_id?: string
  cash_advance_id?: string
  terminal_id?: string
  repayment_date?: string
  created_date?: string
  payment_amount?: number | string
  card_transactions?: number | string
  merchants?: { id?: string; name?: string } | Array<{ id?: string; name?: string }>
  cash_advances?: { id?: string; name?: string } | Array<{ id?: string; name?: string }>
  terminals?: { id?: string; name?: string } | Array<{ id?: string; name?: string }>
}

export interface AllSalesCellFilter {
  merchantId?: string
  cashAdvanceId?: string
  terminalId?: string
  filterByCashAdvance?: boolean
  filterByTerminal?: boolean
  /** IDs from the aggregate payload make drill-downs match the visible cell exactly. */
  repaymentIds?: string[]
  month: string
}

export interface RepaymentFileCommitJob {
  job_id: string
  file_id: string
  status: 'QUEUED' | 'RUNNING'
  committed_rows: number
  total_rows: number
}

export interface RepaymentBulkEditJob {
  job_id: string
  status: 'QUEUED' | 'RUNNING' | 'COMPLETE' | 'FAILED'
  completed_rows: number
  total_rows: number
  error?: string | null
}

interface NamedDropdownItem {
  id?: string
  name?: string
  customer_number?: string
  active_cash_advance?: boolean | null
  merchant_id?: string
  cash_advance_id?: string
  cash_advances?:
    | {
        merchant_id?: string
      }
    | Array<{
        merchant_id?: string
      }>
    | null
}

interface RequestContext {
  controller: AbortController
  signal: AbortSignal
}

interface DropdownPaginationState {
  offset: number
  limit: number
  hasMore: boolean
  loading: boolean
  lastValue?: string
}

type DeletedFilter = { operator: 'eq' | 'neq'; value: boolean }

interface OrderSpec {
  field: string
  direction: 'asc' | 'desc'
  nulls?: 'first' | 'last'
}

type RepaymentsTableName = 'repayments' | 'repayments_staging'

@Injectable({
  providedIn: 'root',
})
export class RepaymentsService implements OnDestroy {
  private static instanceCount = 0
  private readonly instanceId = ++RepaymentsService.instanceCount
  repayments: Repayments = []
  merchants: any[] = []
  terminals: any[] = []
  cashAdvances: any[] = []
  updatedByValues: string[] = []
  private merchantsCacheInitialized = false
  private terminalsCacheInitialized = false
  private cashAdvancesCacheInitialized = false
  private updatedByCacheInitialized = false
  loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true)
  azureAccessToken: string | null = null
  postgrestJwtToken: string | null = null
  sourceFileId: string | null = null
  sourceFileIsCommitted = false
  repaymentsSource: RepaymentsTableName = 'repayments'
  country: string | null = null
  offset = 0
  private hasLoadedAllRepayments = false
  private readonly dropdownPageSize = 100
  private readonly maxRepaymentsInMemoryForAll = 500
  private readonly maxRepaymentsInMemoryForFile = 5000
  // Limit concurrent commit requests so each bounded multi-row transaction
  // can progress without overwhelming the rollup trigger workload.
  private readonly cardOneCommitConcurrency = 8
  private readonly cardOneCommitBatchSize = 5
  private readonly standardCommitBatchSize = 1
  // PostgREST receives these IDs in the query string; keep the URL well below
  // the proxy request-line limit.
  private readonly stagedCleanupBatchSize = 100
  private readonly fileDeleteBatchSize = 5
  private merchantsPagination = this.createDropdownPaginationState()
  private terminalsPagination = this.createDropdownPaginationState()
  private cashAdvancesPagination = this.createDropdownPaginationState()
  private updatedByPagination = this.createDropdownPaginationState()
  private readonly requestControllers = new Map<string, AbortController>()
  private requestSequence = 0
  private readonly destroy$ = new Subject<void>()
  private readonly postgrestBaseUrl = APP_CONFIG.postgrestBaseUrl
  private readonly repaymentsSelectFields = [
    'id',
    'description',
    'comments',
    'repayment_date',
    'previous_balance',
    'payment_amount',
    'balance',
    'card_transactions',
    'data_source',
    'x365_split',
    'merchant_id',
    'cash_advance_id',
    'terminal_id',
    'terminal_name',
    'beneficiary_sort_code',
    'beneficiary_account_number',
    'source_file',
    'changed',
    'deleted',
    'timestamp',
    'created_date',
    'created_date_time',
    'generated_from_repayment_id',
    'updated_by',
    'row_number',
    'country',
    'merchants(name,id)',
    'terminals(name,id)',
    'cash_advances(name,id,active_cash_advance)',
  ]
  private readonly repaymentsSearchView = 'repayments_search'
  private readonly lastCallMap = new Map<string, number>()
  private readonly merchantCache = new Map<
    string,
    { id: string; name?: string }
  >()
  private readonly terminalCache = new Map<
    string,
    { id: string; name?: string }
  >()
  private readonly cashAdvanceCache = new Map<
    string,
    { id: string; name?: string }
  >()
  private relationsPopulated = false

  private getRepaymentSelectFields(table: RepaymentsTableName): string[] {
    // Generated adjustments only exist after a repayment has been committed.
    // The staging table deliberately has no generated_from_repayment_id column.
    return table === 'repayments_staging'
      ? this.repaymentsSelectFields.filter(
          (field) => field !== 'generated_from_repayment_id',
        )
      : this.repaymentsSelectFields
  }

  constructor(
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly queryParamsService: QueryParamsService,
    private readonly tableQueryService: RepaymentsTableQueryService,
  ) {
    if (this.instanceId > 1) {
      console.warn(
        `[RepaymentsService] duplicate instance detected (${this.instanceId})`,
      )
    }
    this.queryParamsService.queryParams$
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (params['country'] !== this.country) {
          this.clearRepaymentsData()
          this.country = params['country']
          this.loadAllRepayments()
        }
      })
  }

  clearRepaymentsData() {
    this.cancelAllRequests({ includeDropdowns: false })
    this.resetRepaymentsCache()
    this.sourceFileId = null
    this.sourceFileIsCommitted = false
    this.tableQueryService.reset()
  }

  public resetRepaymentsCache(): void {
    this.repayments = []
    this.offset = 0
    this.hasLoadedAllRepayments = false
    this.relationsPopulated = false
    this.merchantCache.clear()
    this.terminalCache.clear()
    this.cashAdvanceCache.clear()
    this.updatedByValues = []
    this.updatedByCacheInitialized = false
    this.resetDropdownState(this.updatedByPagination)
  }

  private createDropdownPaginationState(): DropdownPaginationState {
    return {
      offset: 0,
      limit: this.dropdownPageSize,
      hasMore: true,
      loading: false,
    }
  }

  private startRequest(key: string): AbortController {
    const existing = this.requestControllers.get(key)
    if (existing) {
      existing.abort()
    }

    const controller = new AbortController()
    this.requestControllers.set(key, controller)

    return controller
  }

  private clearRequest(key: string, controller: AbortController) {
    const active = this.requestControllers.get(key)
    if (active === controller) {
      this.requestControllers.delete(key)
    }
  }

  private isAbortError(error: unknown): boolean {
    return (
      !!error &&
      typeof error === 'object' &&
      'name' in error &&
      (error as { name?: string }).name === 'AbortError'
    )
  }

  public cancelAllRequests(options?: { includeDropdowns?: boolean }): void {
    const includeDropdowns = options?.includeDropdowns ?? true
    const keys = Array.from(this.requestControllers.keys())
    keys.forEach((key) => {
      const isDropdownRequest =
        key === 'loadAllMerchants' ||
        key === 'loadAllTerminals' ||
        key === 'loadAllCashAdvances' ||
        key === 'loadAllUpdatedBy'

      if (!includeDropdowns && isDropdownRequest) {
        return
      }

      const controller = this.requestControllers.get(key)
      if (controller) {
        try {
          controller.abort()
        } catch {
          // ignore abort errors
        }
        this.requestControllers.delete(key)
      }
    })
  }

  private createRequestContext(
    key: string,
    externalSignal?: AbortSignal,
  ): RequestContext {
    const controller = this.startRequest(key)

    if (externalSignal) {
      if (externalSignal.aborted) {
        controller.abort(externalSignal.reason)
      } else {
        const abort = () => controller.abort(externalSignal.reason)
        externalSignal.addEventListener('abort', abort, { once: true })
        controller.signal.addEventListener(
          'abort',
          () => externalSignal.removeEventListener('abort', abort),
          { once: true },
        )
      }
    }

    return { controller, signal: controller.signal }
  }

  private createUniqueRequestKey(prefix: string): string {
    this.requestSequence += 1
    return `${prefix}:${this.requestSequence}`
  }

  ngOnDestroy(): void {
    this.requestControllers.forEach((controller) => controller.abort())
    this.requestControllers.clear()
    this.destroy$.next()
    this.destroy$.complete()
    RepaymentsService.instanceCount--
    console.warn(
      `[RepaymentsService] destroyed instance ${this.instanceId} remaining=${RepaymentsService.instanceCount}`,
    )
  }

  public updateRepaymentCache(newlyLoadedRepayments: Repayments) {
    // Loop through newly loaded data
    newlyLoadedRepayments.forEach((repayment) => {
      // Find existing record for each of the loaded repayments
      const staleRepayment = this.repayments.find((r) => r.id === repayment.id)

      if (staleRepayment) {
        // Update only the fields that changed
        Object.keys(repayment).forEach((key) => {
          staleRepayment[key] = repayment[key]
        })
      } else {
        // Add new repayment to the existing array (in-place)
        this.repayments.push(repayment)
      }
    })
    this.trimRepaymentsCache()
  }

  private trimRepaymentsCache() {
    const maxSize = this.sourceFileId
      ? this.maxRepaymentsInMemoryForFile
      : this.maxRepaymentsInMemoryForAll
    if (this.repayments.length <= maxSize) {
      return
    }
    const excess = this.repayments.length - maxSize
    if (excess > 0) {
      this.repayments.splice(0, excess)
    }
  }

  public async loadRepaymentsFromFileID(
    fileID: string,
    options?: RequestOptions & { includeActive?: boolean },
  ): Promise<any> {
    const { controller, signal } = this.createRequestContext(
      'loadRepaymentsFromFileID',
      options?.signal,
    )

    this.sourceFileId = fileID
    const includeActive = options?.includeActive ?? true

    let authToken: string | null = null

    return this.authService
      .getPostgrestJwtToken(signal)
      .then((token) => {
        authToken = token

        const queries = [
          {
            key: 'active',
            endpoint: `repayments?select=${this.getRepaymentSelectFields('repayments').join(',')}`,
            include: includeActive,
          },
          {
            key: 'staging',
            endpoint: `repayments_staging?select=${this.getRepaymentSelectFields('repayments_staging').join(',')}`,
            include: !this.sourceFileIsCommitted,
          },
        ]

        const activeQueries = queries.filter((query) => query.include)
        return Promise.all(
          activeQueries.map((query) =>
            this.fetchWithPostgrestAuthRetry(
              `${this.postgrestBaseUrl}/${query.endpoint}&source_file=eq.${fileID}&deleted=eq.false`,
              {
                method: 'GET',
                signal,
              },
              {
                token,
                context: `loading ${query.key} repayments for source file ${fileID}`,
              },
            ).then((response) => response.json()),
          ),
        ).then((responses) => {
          const responseMap = new Map<string, any[]>()
          let responseIndex = 0
          queries.forEach((query) => {
            if (!query.include) {
              responseMap.set(query.key, [])
              return
            }
            responseMap.set(query.key, responses[responseIndex] ?? [])
            responseIndex += 1
          })
          return responseMap
        })
      })
      .then(async (responseMap) => {
        const activeJson = responseMap.get('active') ?? []
        const stagingJson = responseMap.get('staging') ?? []
        const savedFile =
          this.sourceFileIsCommitted ||
          (includeActive &&
          (await this.hasAllActiveStagedRepaymentsBeenCommitted(
            authToken ?? '',
            signal,
          )))
        const repayments = this.dedupeSavedFileRepayments(
          this.normalizeRepayments(activeJson, false),
          this.normalizeRepayments(stagingJson, true),
          savedFile,
        )

        this.updateRepaymentCache(repayments)
        this.offset = repayments.length
        this.hasLoadedAllRepayments = true
        return this.populateRepaymentRelations(
          this.repayments,
          authToken ?? '',
          signal,
        )
      })
      .catch((error) => {
        if (!this.isAbortError(error)) {
          throw error
        }
      })
      .finally(() => {
        this.clearRequest('loadRepaymentsFromFileID', controller)
      })
  }

  public async loadAllRepayments(options?: RequestOptions): Promise<void> {
    this.logRapidCalls('loadAllRepayments')
    // Wait for shared authentication before creating a cancellable data request.
    // Concurrent initial loads otherwise cancel this request while it waits for
    // the JWT, preventing any PostgREST call from being made.
    const token = await this.authService.getPostgrestJwtToken(options?.signal)
    const { controller, signal } = this.createRequestContext(
      'loadAllRepayments',
      options?.signal,
    )

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
      }
      const baseEndpoint = `${this.repaymentsSource}?select=${this.getRepaymentSelectFields(this.repaymentsSource).join(',')}`
      const filterParts: string[] = []

      if (!this.sourceFileId && this.country && this.country !== 'all') {
        filterParts.push(`country=eq.${this.country}`)
      }
      if (this.sourceFileId) {
        filterParts.push(`source_file=eq.${this.sourceFileId}`)
      }
      filterParts.push('deleted=eq.false')

      const repayments = await this.fetchPagedResults({
        endpoint: baseEndpoint,
        headers,
        filter: filterParts.join('&'),
        signal,
        offset: this.offset,
      })

      this.offset += repayments.length
      this.hasLoadedAllRepayments = repayments.length < 100
      this.updateRepaymentCache(repayments)
      await this.populateRepaymentRelations(this.repayments, token, signal)
    } catch (error) {
      if (!this.isAbortError(error)) {
        throw error
      }
    } finally {
      this.clearRequest('loadAllRepayments', controller)
    }
  }

  public async loadAllMerchants(options?: RequestOptions): Promise<any> {
    if (this.merchantsCacheInitialized) {
      return Promise.resolve()
    }

    this.resetDropdownState(this.merchantsPagination)
    this.merchants = []
    return this.fetchMerchants(options).then(() => {
      this.merchantsCacheInitialized = true
    })
  }

  public async loadMoreMerchants(options?: RequestOptions): Promise<any> {
    if (!this.merchantsPagination.hasMore) {
      return Promise.resolve()
    }
    return this.fetchMerchants(options)
  }

  public hasMoreMerchants(): boolean {
    return this.merchantsPagination.hasMore
  }

  public async loadAllTerminals(options?: RequestOptions): Promise<any> {
    if (this.terminalsCacheInitialized) {
      return Promise.resolve()
    }

    this.resetDropdownState(this.terminalsPagination)
    this.terminals = []
    return this.fetchTerminals(options).then(() => {
      this.terminalsCacheInitialized = true
    })
  }

  public async loadMoreTerminals(options?: RequestOptions): Promise<any> {
    if (!this.terminalsPagination.hasMore) {
      return Promise.resolve()
    }
    return this.fetchTerminals(options)
  }

  public hasMoreTerminals(): boolean {
    return this.terminalsPagination.hasMore
  }

  public areMerchantsCached(): boolean {
    return this.merchantsCacheInitialized
  }

  public areTerminalsCached(): boolean {
    return this.terminalsCacheInitialized
  }

  public async loadAllCashAdvances(options?: RequestOptions): Promise<any> {
    if (this.cashAdvancesCacheInitialized) {
      return Promise.resolve()
    }

    this.resetDropdownState(this.cashAdvancesPagination)
    this.cashAdvances = []
    return this.fetchCashAdvances(options).then(() => {
      this.cashAdvancesCacheInitialized = true
    })
  }

  public async loadMoreCashAdvances(options?: RequestOptions): Promise<any> {
    if (!this.cashAdvancesPagination.hasMore) {
      return Promise.resolve()
    }
    return this.fetchCashAdvances(options)
  }

  public hasMoreCashAdvances(): boolean {
    return this.cashAdvancesPagination.hasMore
  }

  public areCashAdvancesCached(): boolean {
    return this.cashAdvancesCacheInitialized
  }

  public searchMerchantsByName(
    term: string,
    options?: RequestOptions,
  ): Promise<
    Array<{
      id?: string
      name: string
      merchantId?: string
      cashAdvanceId?: string
    }>
  > {
    return this.searchDropdownOptions(
      'searchMerchants',
      'merchants',
      term,
      options,
      {},
      ['name', 'customer_number'],
    )
  }

  public async findMerchantsByExactName(
    name: string,
    options?: RequestOptions,
  ): Promise<
    Array<{
      id?: string
      name: string
      merchantId?: string
      cashAdvanceId?: string
    }>
  > {
    const normalizedName = name.trim()
    if (!normalizedName) {
      return []
    }

    const key = `findMerchantsByExactName:${normalizedName}`
    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const params = new URLSearchParams()
      params.set('select', 'id,name')
      params.set('order', 'name.asc')
      params.set('limit', '100')
      params.set('deleted', 'eq.false')
      params.set('name', `eq.${normalizedName}`)

      const response = await fetch(
        `${this.postgrestBaseUrl}/merchants?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        },
      )

      if (!response.ok) {
        throw new Error(
          `Failed to find merchants by exact name: ${response.status} ${response.statusText}`,
        )
      }

      const items: NamedDropdownItem[] = (await response.json()) ?? []
      return this.normalizeNamedDropdownItems(items)
    } catch (error) {
      if (this.isAbortError(error)) {
        throw error
      }
      throw error
    } finally {
      this.clearRequest(key, controller)
    }
  }

  public async findMerchantsByCustomerNumber(
    customerNumber: string,
    options?: RequestOptions,
  ): Promise<Array<{ id?: string; name: string }>> {
    const normalized = customerNumber.trim()
    if (!normalized) {
      return []
    }

    const key = `findMerchantsByCustomerNumber:${normalized}`
    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const params = new URLSearchParams()
      params.set('select', 'id,name')
      params.set('order', 'id.asc')
      params.set('limit', '2')
      params.set('deleted', 'eq.false')
      params.set('customer_number', `eq.${normalized}`)

      const response = await fetch(
        `${this.postgrestBaseUrl}/merchants?${params.toString()}`,
        {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
          signal,
        },
      )
      if (!response.ok) {
        throw new Error(
          `Failed to find merchants by customer number: ${response.status} ${response.statusText}`,
        )
      }

      return this.normalizeNamedDropdownItems((await response.json()) ?? [])
    } finally {
      this.clearRequest(key, controller)
    }
  }

  public findTerminalsByExactName(
    name: string,
    options?: RequestOptions,
  ): Promise<Array<{ id?: string; name: string }>> {
    return this.findDropdownItemsByExactName('terminals', name, options)
  }

  public findCashAdvancesByExactName(
    name: string,
    options?: RequestOptions,
  ): Promise<Array<{ id?: string; name: string }>> {
    return this.findDropdownItemsByExactName('cash_advances', name, options)
  }

  private async findDropdownItemsByExactName(
    endpoint: 'terminals' | 'cash_advances',
    name: string,
    options?: RequestOptions,
  ): Promise<Array<{ id?: string; name: string }>> {
    const normalizedName = name.trim()
    if (!normalizedName) {
      return []
    }
    const { controller, signal } = this.createRequestContext(
      `find${endpoint}:${normalizedName}`,
      options?.signal,
    )
    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const params = new URLSearchParams()
      params.set('select', 'id,name')
      params.set('name', `ilike.${normalizedName}`)
      params.set('limit', '3')
      const response = await fetch(
        `${this.postgrestBaseUrl}/${endpoint}?${params}`,
        {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
          signal,
        },
      )
      if (!response.ok) {
        throw new Error(
          `Failed to find ${endpoint}: ${response.status} ${response.statusText}`,
        )
      }
      return ((await response.json()) ?? [])
        .filter((item) => item?.id && item?.name)
        .map((item) => ({ id: String(item.id), name: String(item.name) }))
    } finally {
      this.clearRequest(`find${endpoint}:${normalizedName}`, controller)
    }
  }

  public searchTerminalsByName(
    term: string,
    options?: RequestOptions,
  ): Promise<
    Array<{
      id?: string
      name: string
      merchantId?: string
      cashAdvanceId?: string
    }>
  > {
    return this.searchDropdownOptions(
      'searchTerminals',
      'terminals',
      term,
      options,
    )
  }

  public searchMerchantScopedTerminalsByName(
    term: string,
    merchantId: string,
    options?: RequestOptions,
  ) {
    return this.searchDropdownOptions(
      `searchTerminalsForMerchant:${merchantId}`,
      'terminals',
      term,
      options,
      {
        'cash_advances.merchant_id': `eq.${merchantId}`,
        cash_advances: 'not.is.null',
      },
    )
  }

  public searchCashAdvanceScopedTerminalsByName(
    term: string,
    cashAdvanceId: string,
    options?: RequestOptions,
  ) {
    return this.searchDropdownOptions(
      `searchTerminalsForCashAdvance:${cashAdvanceId}`,
      'terminals',
      term,
      options,
      { cash_advance_id: `eq.${cashAdvanceId}` },
    )
  }

  public searchCashAdvancesByName(
    term: string,
    options?: RequestOptions,
  ): Promise<
    Array<{
      id?: string
      name: string
      merchantId?: string
      cashAdvanceId?: string
      activeCashAdvance?: boolean
    }>
  > {
    return this.searchDropdownOptions(
      'searchCashAdvances',
      'cash_advances',
      term,
      options,
    )
  }

  public searchMerchantScopedCashAdvancesByName(
    term: string,
    merchantId: string,
    options?: RequestOptions,
  ) {
    return this.searchDropdownOptions(
      `searchCashAdvancesForMerchant:${merchantId}`,
      'cash_advances',
      term,
      options,
      { merchant_id: `eq.${merchantId}` },
    )
  }

  public loadMerchantScopedTerminals(
    merchantIds: string | string[],
    options?: RequestOptions,
  ): Promise<
    Array<{
      id?: string
      name: string
      merchantId?: string
      cashAdvanceId?: string
    }>
  > {
    return this.fetchMerchantScopedDropdownOptions(
      'terminals',
      merchantIds,
      options,
    )
  }

  public loadCashAdvanceScopedTerminals(
    cashAdvanceIds: string | string[],
    options?: RequestOptions,
  ): Promise<
    Array<{
      id?: string
      name: string
      merchantId?: string
      cashAdvanceId?: string
    }>
  > {
    return this.fetchCashAdvanceScopedTerminals(cashAdvanceIds, options)
  }

  public loadMerchantScopedCashAdvances(
    merchantIds: string | string[],
    options?: RequestOptions,
  ): Promise<
    Array<{
      id?: string
      name: string
      merchantId?: string
      cashAdvanceId?: string
      activeCashAdvance?: boolean
    }>
  > {
    return this.fetchMerchantScopedDropdownOptions(
      'cash_advances',
      merchantIds,
      options,
    )
  }

  public async getMerchantIdForTerminal(
    terminalId: string,
    options?: RequestOptions,
  ): Promise<string | undefined> {
    const normalizedTerminalId = terminalId?.trim()
    if (!normalizedTerminalId) {
      return undefined
    }

    const key = `terminalMerchant:${normalizedTerminalId}`
    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const params = new URLSearchParams()
      params.set('select', 'id,cash_advance_id,cash_advances(merchant_id)')
      params.set('id', `eq.${normalizedTerminalId}`)
      params.set('limit', '1')
      params.set('deleted', 'eq.false')

      const response = await fetch(
        `${this.postgrestBaseUrl}/terminals?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        },
      )

      if (!response.ok) {
        throw new Error(
          `Failed to resolve terminal merchant: ${response.status} ${response.statusText}`,
        )
      }

      const records: any[] = (await response.json()) ?? []
      const first = Array.isArray(records) && records.length ? records[0] : null
      const relation = Array.isArray(first?.cash_advances)
        ? first.cash_advances[0]
        : first?.cash_advances

      return typeof relation?.merchant_id === 'string'
        ? relation.merchant_id
        : undefined
    } finally {
      this.clearRequest(key, controller)
    }
  }

  public async getMerchantIdForCashAdvance(
    cashAdvanceId: string,
    options?: RequestOptions,
  ): Promise<string | undefined> {
    const normalizedCashAdvanceId = cashAdvanceId?.trim()
    if (!normalizedCashAdvanceId) {
      return undefined
    }

    const key = `cashAdvanceMerchant:${normalizedCashAdvanceId}`
    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const params = new URLSearchParams()
      params.set('select', 'id,merchant_id')
      params.set('id', `eq.${normalizedCashAdvanceId}`)
      params.set('limit', '1')
      params.set('deleted', 'eq.false')

      const response = await fetch(
        `${this.postgrestBaseUrl}/cash_advances?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        },
      )

      if (!response.ok) {
        throw new Error(
          `Failed to resolve cash advance merchant: ${response.status} ${response.statusText}`,
        )
      }

      const records: any[] = (await response.json()) ?? []
      const first = Array.isArray(records) && records.length ? records[0] : null

      return typeof first?.merchant_id === 'string'
        ? first.merchant_id
        : undefined
    } finally {
      this.clearRequest(key, controller)
    }
  }

  public async loadAllUpdatedBy(options?: RequestOptions): Promise<any> {
    if (this.updatedByCacheInitialized) {
      return Promise.resolve()
    }

    this.resetDropdownState(this.updatedByPagination)
    this.updatedByValues = []
    return this.loadEntireDropdown(
      () => this.fetchUpdatedBy(options),
      this.updatedByPagination,
      () => {
        this.updatedByCacheInitialized = true
      },
    )
  }

  public async loadMoreUpdatedBy(options?: RequestOptions): Promise<any> {
    if (!this.updatedByPagination.hasMore) {
      return Promise.resolve()
    }
    return this.fetchUpdatedBy(options)
  }

  public hasMoreUpdatedBy(): boolean {
    return this.updatedByPagination.hasMore
  }

  public areUpdatedByCached(): boolean {
    return this.updatedByCacheInitialized
  }

  private resetDropdownState(state: DropdownPaginationState) {
    state.offset = 0
    state.hasMore = true
    state.loading = false
    delete state.lastValue
  }

  private async searchDropdownOptions(
    key: string,
    endpoint: string,
    term: string,
    options?: RequestOptions,
    filters: Record<string, string> = {},
    searchFields: string[] = ['name'],
  ): Promise<
    Array<{
      id?: string
      name: string
      merchantId?: string
      cashAdvanceId?: string
    }>
  > {
    const normalizedTerm = term.trim()
    if (!normalizedTerm) {
      return []
    }

    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const params = new URLSearchParams()
      params.set('select', this.getDropdownSelect(endpoint))
      params.set('order', 'name.asc')
      params.set('limit', '50')
      params.set('deleted', 'eq.false')
      const searchExpression = `ilike.*${normalizedTerm}*`
      if (searchFields.length === 1) {
        params.set(searchFields[0], searchExpression)
      } else {
        params.set(
          'or',
          `(${searchFields.map((field) => `${field}.${searchExpression}`).join(',')})`,
        )
      }
      Object.entries(filters).forEach(([field, value]) => {
        params.set(field, value)
      })

      const response = await fetch(
        `${this.postgrestBaseUrl}/${endpoint}?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        },
      )

      if (!response.ok) {
        throw new Error(
          `Failed to search ${endpoint}: ${response.status} ${response.statusText}`,
        )
      }

      const items: NamedDropdownItem[] = (await response.json()) ?? []
      return this.normalizeNamedDropdownItems(items)
    } catch (error) {
      if (this.isAbortError(error)) {
        throw error
      }
      throw error
    } finally {
      this.clearRequest(key, controller)
    }
  }

  private async loadEntireDropdown(
    fetchPage: () => Promise<any>,
    state: DropdownPaginationState,
    onComplete: () => void,
  ): Promise<void> {
    while (state.hasMore) {
      const previousOffset = state.offset
      const previousHasMore = state.hasMore
      await fetchPage()
      if (
        state.loading ||
        (state.offset === previousOffset && state.hasMore === previousHasMore)
      ) {
        break
      }
    }

    if (!state.hasMore) {
      onComplete()
    }
  }

  private async fetchMerchants(options?: RequestOptions): Promise<any> {
    return this.fetchDropdownPage({
      key: 'loadAllMerchants',
      endpoint: 'merchants',
      state: this.merchantsPagination,
      accumulator: (items) => {
        this.merchants = items
      },
      options,
    })
  }

  private async fetchTerminals(options?: RequestOptions): Promise<any> {
    return this.fetchDropdownPage({
      key: 'loadAllTerminals',
      endpoint: 'terminals',
      state: this.terminalsPagination,
      accumulator: (items) => {
        this.terminals = items
      },
      options,
    })
  }

  private async fetchCashAdvances(options?: RequestOptions): Promise<any> {
    return this.fetchDropdownPage({
      key: 'loadAllCashAdvances',
      endpoint: 'cash_advances',
      state: this.cashAdvancesPagination,
      accumulator: (items) => {
        this.cashAdvances = items
      },
      options,
    })
  }

  private async fetchUpdatedBy(options?: RequestOptions): Promise<any> {
    const state = this.updatedByPagination
    if (state.loading || !state.hasMore) {
      return Promise.resolve()
    }

    const countrySnapshot = this.country
    const sourceFileSnapshot = this.sourceFileId

    const { controller, signal } = this.createRequestContext(
      'loadAllUpdatedBy',
      options?.signal,
    )

    const offset = state.offset
    const limit = state.limit
    state.loading = true

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const params = new URLSearchParams()
      params.set('select', 'updated_by')
      params.set('order', 'updated_by.asc')
      params.set('limit', String(limit))
      // This query uses a value cursor (updated_by), so an SQL offset would
      // skip options after the first page.
      params.set('offset', '0')
      // Rows are ordered by updater, so each page can contain many duplicate
      // names. Advance past the last name rather than paging through every
      // repayment solely to populate this filter's distinct options.
      params.set(
        'updated_by',
        state.lastValue ? `gt.${state.lastValue}` : 'not.is.null',
      )

      const baseParams = this.buildBaseQueryParams()
      Object.entries(baseParams).forEach(([key, value]) => {
        if (value) {
          params.set(key, value)
        }
      })

      const response = await fetch(
        `${this.postgrestBaseUrl}/repayments?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        },
      )

      if (!response.ok) {
        throw new Error(
          `Failed to load updated by values: ${response.status} ${response.statusText}`,
        )
      }

      const newItems: Array<{ updated_by?: string }> =
        (await response.json()) ?? []

      if (
        countrySnapshot !== this.country ||
        sourceFileSnapshot !== this.sourceFileId
      ) {
        return
      }

      const newValues = newItems
        .map((item) => item.updated_by)
        .filter((value): value is string => !!value)
      const combined = [...this.updatedByValues, ...newValues]
      this.updatedByValues = Array.from(new Set(combined)).sort((a, b) =>
        a.localeCompare(b),
      )

      const fetched = Array.isArray(newItems) ? newItems.length : 0
      state.offset = offset + fetched
      const lastValue = newValues[newValues.length - 1]
      if (lastValue) {
        state.lastValue = lastValue
      }
      state.hasMore = fetched === limit
    } catch (error) {
      if (!this.isAbortError(error)) {
        throw error
      }
    } finally {
      state.loading = false
      this.clearRequest('loadAllUpdatedBy', controller)
    }
  }

  private async fetchMerchantScopedDropdownOptions(
    endpoint: 'terminals' | 'cash_advances',
    merchantIds: string | string[],
    options?: RequestOptions,
  ): Promise<
    Array<{
      id?: string
      name: string
      merchantId?: string
      cashAdvanceId?: string
    }>
  > {
    const normalizedMerchantIds = Array.from(
      new Set(
        (Array.isArray(merchantIds) ? merchantIds : [merchantIds])
          .map((merchantId) => String(merchantId ?? '').trim())
          .filter(Boolean),
      ),
    )
    if (normalizedMerchantIds.length === 0) {
      return []
    }

    const merchantFilterValue =
      normalizedMerchantIds.length === 1
        ? `eq.${normalizedMerchantIds[0]}`
        : `in.(${normalizedMerchantIds.join(',')})`
    const key = `merchantScoped:${endpoint}:${normalizedMerchantIds.join(',')}`
    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const limit = this.dropdownPageSize
      let offset = 0
      let collected: NamedDropdownItem[] = []

      while (true) {
        const queryParams = new URLSearchParams()
        queryParams.set('select', this.getDropdownSelect(endpoint))
        queryParams.set('order', 'name.asc')
        queryParams.set('limit', String(limit))
        queryParams.set('offset', String(offset))
        queryParams.set('deleted', 'eq.false')

        if (endpoint === 'cash_advances') {
          queryParams.set('merchant_id', merchantFilterValue)
        } else {
          queryParams.set('cash_advances.merchant_id', merchantFilterValue)
          queryParams.set('cash_advances', 'not.is.null')
        }

        const response = await fetch(
          `${this.postgrestBaseUrl}/${endpoint}?${queryParams.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal,
          },
        )

        if (!response.ok) {
          throw new Error(
            `Failed to load ${endpoint} for merchant ${normalizedMerchantIds.join(',')}: ${response.status} ${response.statusText}`,
          )
        }

        const pageItems: NamedDropdownItem[] = (await response.json()) ?? []
        collected = this.mergeDropdownItems(collected, pageItems)

        if (pageItems.length < limit) {
          break
        }

        offset += pageItems.length
      }

      return this.normalizeNamedDropdownItems(collected)
    } catch (error) {
      throw error
    } finally {
      this.clearRequest(key, controller)
    }
  }

  private async fetchCashAdvanceScopedTerminals(
    cashAdvanceIds: string | string[],
    options?: RequestOptions,
  ): Promise<
    Array<{
      id?: string
      name: string
      merchantId?: string
      cashAdvanceId?: string
    }>
  > {
    const normalizedIds = Array.from(
      new Set(
        (Array.isArray(cashAdvanceIds) ? cashAdvanceIds : [cashAdvanceIds])
          .map((id) => String(id ?? '').trim())
          .filter(Boolean),
      ),
    )
    if (normalizedIds.length === 0) {
      return []
    }

    const filterValue =
      normalizedIds.length === 1
        ? `eq.${normalizedIds[0]}`
        : `in.(${normalizedIds.join(',')})`
    const key = `cashAdvanceScoped:terminals:${normalizedIds.join(',')}`
    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const limit = this.dropdownPageSize
      let offset = 0
      let collected: NamedDropdownItem[] = []

      while (true) {
        const queryParams = new URLSearchParams()
        queryParams.set('select', this.getDropdownSelect('terminals'))
        queryParams.set('order', 'name.asc')
        queryParams.set('limit', String(limit))
        queryParams.set('offset', String(offset))
        queryParams.set('deleted', 'eq.false')
        queryParams.set('cash_advance_id', filterValue)

        const response = await fetch(
          `${this.postgrestBaseUrl}/terminals?${queryParams.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal,
          },
        )

        if (!response.ok) {
          throw new Error(
            `Failed to load terminals for cash advance ${normalizedIds.join(',')}: ${response.status} ${response.statusText}`,
          )
        }

        const pageItems: NamedDropdownItem[] = (await response.json()) ?? []
        collected = this.mergeDropdownItems(collected, pageItems)

        if (pageItems.length < limit) {
          break
        }

        offset += pageItems.length
      }

      return this.normalizeNamedDropdownItems(collected)
    } catch (error) {
      throw error
    } finally {
      this.clearRequest(key, controller)
    }
  }

  private async fetchDropdownPage(params: {
    key: string
    endpoint: string
    state: DropdownPaginationState
    accumulator: (items: any[]) => void
    options?: RequestOptions
  }): Promise<any> {
    const { key, endpoint, state, accumulator, options } = params

    if (state.loading || !state.hasMore) {
      return Promise.resolve()
    }

    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    const offset = state.offset
    const limit = state.limit
    state.loading = true

    return this.authService
      .getPostgrestJwtToken(signal)
      .then((token) => {
        const queryParams = new URLSearchParams()
        queryParams.set('select', this.getDropdownSelect(endpoint))
        queryParams.set('order', 'name.asc')
        queryParams.set('limit', String(limit))
        queryParams.set('offset', String(offset))
        queryParams.set('deleted', 'eq.false')

        return this.fetchWithPostgrestAuthRetry(
          `${this.postgrestBaseUrl}/${endpoint}?${queryParams.toString()}`,
          {
            method: 'GET',
            signal,
          },
          {
            token,
            context: `loading ${endpoint} dropdown options`,
          },
        )
      })
      .then((response) => response.json())
      .then((newItems) => {
        const existing = this.getExistingDropdownItems(endpoint)
        const nextItems = Array.isArray(newItems) ? newItems : []
        const merged = this.mergeDropdownItems(existing, nextItems)
        accumulator(merged)

        const fetched = nextItems.length
        const added = merged.length - existing.length
        state.offset = offset + fetched
        state.hasMore = fetched === limit && added > 0
      })
      .catch((error) => {
        if (!this.isAbortError(error)) {
          throw error
        }
      })
      .finally(() => {
        state.loading = false
        this.clearRequest(key, controller)
      })
  }

  private getExistingDropdownItems(endpoint: string): any[] {
    switch (endpoint) {
      case 'merchants':
        return this.merchants ?? []
      case 'terminals':
        return this.terminals ?? []
      case 'cash_advances':
        return this.cashAdvances ?? []
      default:
        return []
    }
  }

  private mergeDropdownItems(existing: any[], incoming: any[]): any[] {
    if (!existing.length) {
      return incoming
    }

    const merged = [...existing]
    const seen = new Set<string>()
    existing.forEach((item) => {
      if (item?.id) {
        seen.add(String(item.id))
      }
    })

    incoming.forEach((item) => {
      const id = item?.id ? String(item.id) : ''
      if (id && !seen.has(id)) {
        merged.push(item)
        seen.add(id)
      }
    })

    return merged
  }

  private normalizeNamedDropdownItems(items: NamedDropdownItem[]): Array<{
    id?: string
    name: string
    customerNumber?: string
    merchantId?: string
    cashAdvanceId?: string
    activeCashAdvance?: boolean
  }> {
    const seen = new Set<string>()

    return items
      .map((item) => ({
        id: item?.id ? String(item.id) : undefined,
        name: item?.name ? String(item.name).trim() : '',
        customerNumber: item?.customer_number
          ? String(item.customer_number).trim()
          : undefined,
        merchantId: this.getDropdownItemMerchantId(item),
        cashAdvanceId: item?.cash_advance_id
          ? String(item.cash_advance_id)
          : undefined,
        activeCashAdvance:
          typeof item?.active_cash_advance === 'boolean'
            ? item.active_cash_advance
            : undefined,
      }))
      .filter((item) => {
        if (!item.name) {
          return false
        }

        const key = item.id ?? item.name
        if (seen.has(key)) {
          return false
        }

        seen.add(key)
        return true
      })
  }

  private getDropdownSelect(endpoint: string): string {
    switch (endpoint) {
      case 'merchants':
        return 'id,name,customer_number'
      case 'terminals':
        return 'id,name,cash_advance_id,cash_advances(merchant_id)'
      case 'cash_advances':
        return 'id,name,merchant_id,active_cash_advance'
      default:
        return 'id,name'
    }
  }

  private getDropdownItemMerchantId(
    item: NamedDropdownItem,
  ): string | undefined {
    if (item?.merchant_id) {
      return String(item.merchant_id)
    }

    const relation = Array.isArray(item?.cash_advances)
      ? item.cash_advances[0]
      : item?.cash_advances

    return relation?.merchant_id ? String(relation.merchant_id) : undefined
  }

  async addRepayments(repayments: Repayments, options?: RequestOptions) {
    const { controller, signal } = this.createRequestContext(
      'addRepayments',
      options?.signal,
    )

    try {
      if (!Array.isArray(repayments) || repayments.length === 0) {
        return []
      }

      const token = await this.authService.getPostgrestJwtToken(signal)
      const activeRepayments = repayments.filter(
        (repayment) =>
          this.resolveRepaymentMutationTable(repayment) === 'repayments',
      )
      const stagedRepayments = repayments.filter(
        (repayment) =>
          this.resolveRepaymentMutationTable(repayment) !== 'repayments',
      )

      const createInTable = async (
        table: RepaymentsTableName,
        rows: Repayments,
      ): Promise<Repayments> => {
        if (!rows.length) {
          return []
        }

        const payload = this.normalizeBulkInsertPayload(rows)
        const response = await fetch(`${this.postgrestBaseUrl}/${table}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Prefer: 'return=representation',
          },
          body: JSON.stringify(payload),
          signal,
        })

        if (!response.ok) {
          throw new Error(
            `Failed to add repayments: ${response.status} ${response.statusText}`,
          )
        }

        const created = await response.json().catch(() => null)
        if (!Array.isArray(created) || created.length === 0) {
          return []
        }

        return this.normalizeRepayments(created, table === 'repayments_staging')
      }

      const [createdActive, createdStaged] = await Promise.all([
        createInTable('repayments', activeRepayments),
        createInTable('repayments_staging', stagedRepayments),
      ])

      const created = [...createdActive, ...createdStaged]
      if (created.length) {
        this.updateRepaymentCache(created)
      }
      return created
    } catch (error) {
      if (!this.isAbortError(error)) {
        throw error
      }
    } finally {
      this.clearRequest('addRepayments', controller)
    }
  }

  async updateRepayment(repayment: Repayment, options?: RequestOptions) {
    const { controller, signal } = this.createRequestContext(
      'updateRepayment',
      options?.signal,
    )

    const payload = this.normalizeRepaymentForPostgrest(repayment)
    payload.updated_by = this.authService.getAuditUser()
    payload.last_changed_in = 'AWS'

    try {
      const id = repayment?.id ? String(repayment.id) : ''
      if (!id) {
        return null
      }

      const token = await this.authService.getPostgrestJwtToken(signal)
      const preferredTable = this.resolveRepaymentMutationTable(repayment)
      const tables: RepaymentsTableName[] = preferredTable
        ? [
            preferredTable,
            preferredTable === 'repayments'
              ? 'repayments_staging'
              : 'repayments',
          ]
        : ['repayments', 'repayments_staging']

      for (const table of tables) {
        const updated = await this.patchRepaymentsByIds(
          table,
          [id],
          payload,
          token,
          signal,
        )
        if (updated.length > 0) {
          this.updateRepaymentCache(updated)
          return updated[0] as Repayment
        }
      }

      return null
    } catch (error) {
      if (!this.isAbortError(error)) {
        throw error
      }
      return null
    } finally {
      this.clearRequest('updateRepayment', controller)
    }
  }

  async bulkUpdateRepayments(
    repayments: Repayments,
    updateBody: Object,
    options?: RequestOptions,
  ) {
    const { controller, signal } = this.createRequestContext(
      'bulkUpdateRepayments',
      options?.signal,
    )

    const normalizedUpdate =
      'repayment_date' in updateBody
        ? {
            ...updateBody,
            repayment_date: this.formatDateForPostgrest(
              (updateBody as any).repayment_date,
            ),
          }
        : updateBody

    try {
      const activeIds = new Set<string>()
      const stagedIds = new Set<string>()

      repayments.forEach((repayment) => {
        const id = repayment?.id ? String(repayment.id) : ''
        if (!id) {
          return
        }
        if (
          this.resolveRepaymentMutationTable(repayment) === 'repayments_staging'
        ) {
          stagedIds.add(id)
          return
        }
        activeIds.add(id)
      })

      if (activeIds.size === 0 && stagedIds.size === 0) {
        return
      }

      const token = await this.authService.getPostgrestJwtToken(signal)
      const updatePayload = normalizedUpdate as Partial<Repayment>
      const updatedRows: Repayments = []

      const patchIdsInTable = async (
        table: RepaymentsTableName,
        ids: Set<string>,
      ): Promise<Set<string>> => {
        if (ids.size === 0) {
          return new Set<string>()
        }
        const rows = await this.patchRepaymentsByIds(
          table,
          Array.from(ids),
          updatePayload,
          token,
          signal,
        )
        if (rows.length > 0) {
          updatedRows.push(...rows)
        }
        return this.collectRepaymentIds(rows)
      }

      const matchedActiveInRepayments = await patchIdsInTable(
        'repayments',
        activeIds,
      )
      const matchedStagedInStaging = await patchIdsInTable(
        'repayments_staging',
        stagedIds,
      )

      const fallbackToStaging = new Set(
        Array.from(activeIds).filter(
          (id) => !matchedActiveInRepayments.has(id),
        ),
      )
      const fallbackToRepayments = new Set(
        Array.from(stagedIds).filter((id) => !matchedStagedInStaging.has(id)),
      )

      await patchIdsInTable('repayments_staging', fallbackToStaging)
      await patchIdsInTable('repayments', fallbackToRepayments)

      if (updatedRows.length > 0) {
        this.updateRepaymentCache(updatedRows)
      }
    } catch (error) {
      if (!this.isAbortError(error)) {
        throw error
      }
    } finally {
      this.clearRequest('bulkUpdateRepayments', controller)
    }
  }

  async requestBulkRepaymentEdit(repayments: Repayments, update: Partial<Repayment>): Promise<RepaymentBulkEditJob> {
    const repaymentIds = Array.from(new Set(repayments.map((repayment) => String(repayment.id ?? '')).filter(Boolean)))
    if (!repaymentIds.length) throw new Error('Select at least one repayment')
    const token = await this.authService.getPostgrestJwtToken()
    // The bulk-edit dialog uses the UK display format (dd/MM/yyyy). Send an
    // unambiguous ISO date to the persisted job; PostgreSQL otherwise follows
    // its session DateStyle and can read 08/09 as 9 August.
    const payload = 'repayment_date' in update
      ? { ...update, repayment_date: this.formatDateForPostgrest(update.repayment_date) }
      : update
    const response = await fetch(`${this.postgrestBaseUrl}/rpc/request_repayment_bulk_edit`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ p_repayment_ids: repaymentIds, p_update: payload }),
    })
    if (!response.ok) throw new Error(`Failed to queue repayment update: ${response.status} ${response.statusText}`)
    const [job] = await response.json()
    if (!job?.job_id) throw new Error('The repayment update job was not created')
    return job as RepaymentBulkEditJob
  }

  async requestBulkRepaymentEditForFile(fileId: string, update: Partial<Repayment>): Promise<RepaymentBulkEditJob> {
    if (!fileId) throw new Error('Select a repayment file')
    const token = await this.authService.getPostgrestJwtToken()
    const payload = 'repayment_date' in update
      ? { ...update, repayment_date: this.formatDateForPostgrest(update.repayment_date) }
      : update
    const response = await fetch(`${this.postgrestBaseUrl}/rpc/request_repayment_file_bulk_edit`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ p_file_id: fileId, p_update: payload }),
    })
    if (!response.ok) throw new Error(`Failed to queue repayment update: ${response.status} ${response.statusText}`)
    const [job] = await response.json()
    if (!job?.job_id) throw new Error('The repayment update job was not created')
    return job as RepaymentBulkEditJob
  }

  async getRepaymentBulkEditJob(jobId: string): Promise<RepaymentBulkEditJob | null> {
    const token = await this.authService.getPostgrestJwtToken()
    const response = await fetch(`${this.postgrestBaseUrl}/rpc/get_repayment_bulk_edit_job`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ p_job_id: jobId }),
    })
    if (!response.ok) throw new Error(`Failed to read repayment update progress: ${response.status} ${response.statusText}`)
    const [job] = await response.json()
    return job ?? null
  }

  async getActiveRepaymentBulkEditJobs(): Promise<RepaymentBulkEditJob[]> {
    const token = await this.authService.getPostgrestJwtToken()
    const response = await fetch(`${this.postgrestBaseUrl}/rpc/get_active_repayment_bulk_edit_jobs`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: '{}',
    })
    if (!response.ok) throw new Error(`Failed to read repayment update progress: ${response.status} ${response.statusText}`)
    return await response.json()
  }

  private async patchRepaymentsByIds(
    table: RepaymentsTableName,
    ids: string[],
    payload: Partial<Repayment>,
    token: string,
    signal?: AbortSignal,
  ): Promise<Repayments> {
    if (!ids.length) {
      return []
    }

    const params = new URLSearchParams()
    params.set('id', `in.(${ids.join(',')})`)
    // A database trigger can assign related records (such as the active cash
    // advance) during this update. Return their display data with the row so
    // the table never has to fall back to rendering an opaque ID.
    params.set(
      'select',
      '*,merchants(name,id),terminals(name,id),cash_advances(name,id)',
    )

    const response = await fetch(
      `${this.postgrestBaseUrl}/${table}?${params.toString()}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Prefer: 'return=representation',
        },
        body: JSON.stringify(payload),
        signal,
      },
    )

    if (!response.ok) {
      throw new Error(
        `Failed to update ${table}: ${response.status} ${response.statusText}`,
      )
    }

    const updated = await response.json().catch(() => null)
    return Array.isArray(updated) ? updated : []
  }

  private collectRepaymentIds(repayments: Repayments): Set<string> {
    const ids = new Set<string>()
    repayments.forEach((repayment) => {
      const id = repayment?.id ? String(repayment.id) : ''
      if (id) {
        ids.add(id)
      }
    })
    return ids
  }

  async deleteRepaymentsByFileID(ids: Array<string>, options?: RequestOptions) {
    const { controller, signal } = this.createRequestContext(
      'deleteRepaymentsByFileID',
      options?.signal,
    )

    const joinedIds = ids.map((id) => `${id}`).join(',')

    return this.authService
      .getPostgrestJwtToken(signal)
      .then(async (token) => {
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }

        const payload = JSON.stringify({
          deleted: true,
          updated_by: this.authService.getAuditUser(),
          last_changed_in: 'AWS',
        })
        const findActiveIds = async (
          table: 'repayments' | 'repayments_staging',
        ): Promise<string[]> => {
          const rows = await this.fetchAllPages({
            endpoint: `${table}?select=id&source_file=in.(${joinedIds})&deleted=is.false`,
            headers,
            signal,
            limit: 1000,
          })
          return rows
            .map((row) => row?.id)
            .filter((id): id is string => typeof id === 'string' && !!id)
        }

        const activeRepaymentIds = await findActiveIds('repayments')
        const stagedRepaymentIds = await findActiveIds('repayments_staging')
        const total = activeRepaymentIds.length + stagedRepaymentIds.length
        let completed = 0
        const reportProgress = () =>
          options?.onProgress?.(completed, total || 1)
        reportProgress()

        const softDelete = async (
          table: 'repayments' | 'repayments_staging',
          repaymentIds: string[],
        ) => {
          for (
            let offset = 0;
            offset < repaymentIds.length;
            offset += this.fileDeleteBatchSize
          ) {
            const batch = repaymentIds.slice(
              offset,
              offset + this.fileDeleteBatchSize,
            )
            const response = await fetch(
              `${this.postgrestBaseUrl}/${table}?id=in.(${batch.join(',')})&deleted=is.false`,
              {
                method: 'PATCH',
                headers: { ...headers, Prefer: 'return=minimal' },
                body: payload,
                signal,
              },
            )
            if (!response.ok) {
              throw new Error(
                `Failed to delete ${table}: ${response.status} ${response.statusText}`,
              )
            }
            completed += batch.length
            reportProgress()
          }
        }

        // Keep each mutation small: large statement-level trigger transition
        // tables can exceed the gateway timeout for CardOne files. Run the
        // active and staging tables sequentially to avoid competing rollups.
        await softDelete('repayments', activeRepaymentIds)
        await softDelete('repayments_staging', stagedRepaymentIds)
      })
      .then(() => {
        this.removeRepaymentsByFileIDs(ids)
      })
      .catch((error) => {
        if (!this.isAbortError(error)) {
          throw error
        }
      })
      .finally(() => {
        this.clearRequest('deleteRepaymentsByFileID', controller)
      })
  }

  private removeRepaymentsByFileIDs(ids: Array<string>) {
    if (!ids?.length || !this.repayments?.length) {
      return
    }
    const idSet = new Set(ids)
    for (let i = 0; i < this.repayments.length; i += 1) {
      if (idSet.has(this.repayments[i]?.source_file)) {
        this.repayments[i].deleted = true
      }
    }
  }

  async deleteRepayments(repayments: Repayments, options?: RequestOptions) {
    const { controller, signal } = this.createRequestContext(
      'deleteRepayments',
      options?.signal,
    )

    const repaymentsToDelete = repayments.filter((repayment) => !!repayment)
    if (!repaymentsToDelete.length) {
      this.clearRequest('deleteRepayments', controller)
      return Promise.resolve()
    }
    return this.authService
      .getPostgrestJwtToken(signal)
      .then(async (token) => {
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }

        const activeIds = repaymentsToDelete
          .filter(
            (repayment) =>
              this.resolveRepaymentMutationTable(repayment) === 'repayments',
          )
          .map((repayment) => repayment.id)
          .filter((id): id is string => !!id)
        const stagedIds = repaymentsToDelete
          .filter(
            (repayment) =>
              this.resolveRepaymentMutationTable(repayment) ===
              'repayments_staging',
          )
          .map((repayment) => repayment.id)
          .filter((id): id is string => !!id)

        if (activeIds.length > 0) {
          const response = await fetch(
            `${this.postgrestBaseUrl}/repayments?id=in.(${activeIds.join(',')})`,
            {
              method: 'PATCH',
              headers,
              body: JSON.stringify({
                deleted: true,
                updated_by: this.authService.getAuditUser(),
                last_changed_in: 'AWS',
              }),
              signal,
            },
          )
          if (!response.ok) {
            throw new Error(
              `Failed to delete repayments: ${response.status} ${response.statusText}`,
            )
          }
        }

        if (stagedIds.length > 0) {
          const response = await fetch(
            `${this.postgrestBaseUrl}/repayments_staging?id=in.(${stagedIds.join(',')})`,
            {
              method: 'PATCH',
              headers,
              body: JSON.stringify({
                deleted: true,
                updated_by: this.authService.getAuditUser(),
                last_changed_in: 'AWS',
              }),
              signal,
            },
          )
          if (!response.ok) {
            throw new Error(
              `Failed to delete staged repayments: ${response.status} ${response.statusText}`,
            )
          }
        }
      })
      .catch((error) => {
        if (!this.isAbortError(error)) {
          throw error
        }
      })
      .finally(() => {
        this.clearRequest('deleteRepayments', controller)
      })
  }

  async undoDeleteRepayments(repayments: Repayments, options?: RequestOptions) {
    const { controller, signal } = this.createRequestContext(
      'undoDeleteRepayments',
      options?.signal,
    )

    const repaymentsToUndelete = repayments.filter((repayment) => !!repayment)
    return this.authService
      .getPostgrestJwtToken(signal)
      .then(async (token) => {
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }

        const activeIds = repaymentsToUndelete
          .filter(
            (repayment) =>
              this.resolveRepaymentMutationTable(repayment) === 'repayments',
          )
          .map((repayment) => repayment.id)
          .filter((id): id is string => !!id)
        const stagedIds = repaymentsToUndelete
          .filter(
            (repayment) =>
              this.resolveRepaymentMutationTable(repayment) ===
              'repayments_staging',
          )
          .map((repayment) => repayment.id)
          .filter((id): id is string => !!id)
        const undeletePayload = {
          deleted: false,
          updated_by: this.authService.getAuditUser(),
          last_changed_in: 'AWS',
        }

        if (activeIds.length > 0) {
          await fetch(
            `${this.postgrestBaseUrl}/repayments?id=in.(${activeIds.join(',')})`,
            {
              method: 'PATCH',
              headers,
              body: JSON.stringify(undeletePayload),
              signal,
            },
          )
        }

        if (stagedIds.length > 0) {
          await fetch(
            `${this.postgrestBaseUrl}/repayments_staging?id=in.(${stagedIds.join(',')})`,
            {
              method: 'PATCH',
              headers,
              body: JSON.stringify(undeletePayload),
              signal,
            },
          )
        }
      })
      .catch((error) => {
        if (!this.isAbortError(error)) {
          throw error
        }
      })
      .finally(() => {
        this.clearRequest('undoDeleteRepayments', controller)
      })
  }

  async promoteRepaymentFromStaging(
    repayment: Repayment,
    options?: RequestOptions,
  ): Promise<void> {
    const key = this.createUniqueRequestKey('promoteRepaymentFromStaging')
    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }

      const normalized = this.normalizeRepaymentForPostgrest({
        ...repayment,
        updated_by: this.authService.getAuditUser(),
      })

      const createResponse = await fetch(
        `${this.postgrestBaseUrl}/repayments`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(normalized),
          signal,
        },
      )
      if (!createResponse.ok) {
        throw new Error(
          `Failed to commit repayment: ${createResponse.status} ${createResponse.statusText}`,
        )
      }

      const deleteResponse = await fetch(
        `${this.postgrestBaseUrl}/repayments_staging?id=eq.${encodeURIComponent(repayment.id!)}`,
        {
          method: 'DELETE',
          headers,
          signal,
        },
      )
      if (!deleteResponse.ok) {
        throw new Error(
          `Repayment was committed but staging cleanup failed: ${deleteResponse.status} ${deleteResponse.statusText}`,
        )
      }

      this.resetRepaymentsCache()
    } catch (error) {
      if (!this.isAbortError(error)) {
        throw error
      }
    } finally {
      this.clearRequest(key, controller)
    }
  }

  async stageRepayments(fileID: string, options?: RequestOptions) {
    const { controller, signal } = this.createRequestContext(
      'stageRepayments',
      options?.signal,
    )
    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
      const request = await fetch(
        `${this.postgrestBaseUrl}/rpc/request_repayment_file_commit`,
        { method: 'POST', headers, body: JSON.stringify({ p_file_id: fileID }), signal },
      )
      if (!request.ok) throw new Error(`Failed to queue file commit: ${request.status} ${request.statusText}`)
      const [job] = await request.json()
      if (!job?.job_id) throw new Error('The commit job was not created')
      options?.onQueued?.()
      while (true) {
        const response = await fetch(`${this.postgrestBaseUrl}/rpc/get_repayment_file_commit_job`, {
          method: 'POST', headers, body: JSON.stringify({ p_job_id: job.job_id }), signal,
        })
        if (!response.ok) throw new Error(`Failed to read file commit progress: ${response.status} ${response.statusText}`)
        const [current] = await response.json()
        if (!current) throw new Error('The commit job no longer exists')
        options?.onProgress?.(Number(current.committed_rows ?? 0), Number(current.total_rows ?? 1))
        if (current.status === 'COMPLETE') return
        if (current.status === 'FAILED') throw new Error(current.error || 'File commit failed')
        await new Promise<void>((resolve, reject) => {
          const timer = setTimeout(resolve, 1000)
          signal.addEventListener('abort', () => { clearTimeout(timer); reject(signal.reason) }, { once: true })
        })
      }
    } finally {
      this.clearRequest('stageRepayments', controller)
    }
  }

  /**
   * Returns persisted progress for files whose commits are still running. This
   * deliberately does not rely on a browser-held job id, so a new page load
   * can resume showing progress for a commit started in an earlier session.
   */
  async getActiveFileCommitJobs(
    fileIDs: string[],
    options?: Pick<RequestOptions, 'signal'>,
  ): Promise<RepaymentFileCommitJob[]> {
    if (fileIDs.length === 0) {
      return []
    }

    const { controller, signal } = this.createRequestContext(
      'getActiveFileCommitJobs',
      options?.signal,
    )
    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const response = await fetch(
        `${this.postgrestBaseUrl}/rpc/get_active_repayment_file_commit_jobs`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ p_file_ids: fileIDs }),
          signal,
        },
      )
      if (!response.ok) {
        throw new Error(
          `Failed to read file commit progress: ${response.status} ${response.statusText}`,
        )
      }
      return await response.json()
    } finally {
      this.clearRequest('getActiveFileCommitJobs', controller)
    }
  }

  // Retained temporarily for rollback/reference; the browser no longer calls it.
  private async legacyStageRepayments(fileID: string, options?: RequestOptions) {
    const { controller, signal } = this.createRequestContext(
      'stageRepayments',
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
      const selectFields = [
        'id',
        'description',
        'comments',
        'repayment_date',
        'payment_amount',
        'data_source',
        'x365_split',
        'merchant_id',
        'cash_advance_id',
        'terminal_id',
        'beneficiary_sort_code',
        'beneficiary_account_number',
        'source_file',
        'row_number',
        'updated_by',
        'country',
      ]
      const select = encodeURIComponent(selectFields.join(','))
      const stagingResponse = await fetch(
        `${this.postgrestBaseUrl}/repayments_staging?select=${select}&source_file=eq.${encodeURIComponent(fileID)}&deleted=eq.false`,
        {
          method: 'GET',
          headers: { ...headers, Range: '0-999999' },
          signal,
        },
      )
      if (!stagingResponse.ok) {
        throw new Error(
          `Failed to load repayments to commit: ${stagingResponse.status} ${stagingResponse.statusText}`,
        )
      }

      const repaymentsToSave = await stagingResponse.json()
      if (!Array.isArray(repaymentsToSave) || !repaymentsToSave.length) {
        return
      }

      let completed = 0
      const totalProgressSteps = repaymentsToSave.length * 2
      const reportProgress = () =>
        options?.onProgress?.(completed, totalProgressSteps)
      reportProgress()

      const commitBatchSize = this.isCardOneCommit(repaymentsToSave)
        ? this.cardOneCommitBatchSize
        : this.standardCommitBatchSize
      const commitBatches: Repayments[] = []
      for (
        let offset = 0;
        offset < repaymentsToSave.length;
        offset += commitBatchSize
      ) {
        commitBatches.push(
          repaymentsToSave.slice(offset, offset + commitBatchSize),
        )
      }

      const committedIds: string[] = []
      let nextBatchIndex = 0
      let failure: unknown
      const worker = async () => {
        while (!failure && nextBatchIndex < commitBatches.length) {
          const batch = commitBatches[nextBatchIndex++]
          try {
            await this.commitStagedRepayments(batch, headers, signal)
            committedIds.push(...batch.map(({ id }) => id!))
            completed += batch.length
            reportProgress()
          } catch (error) {
            failure = error
            return
          }
        }
      }
      // CardOne benefits from bounded parallelism. All other sources still use
      // one-row requests, one at a time, to avoid trigger-lock contention.
      const concurrency = this.isCardOneCommit(repaymentsToSave)
        ? this.cardOneCommitConcurrency
        : 1
      await Promise.all(
        Array.from(
          { length: Math.min(concurrency, commitBatches.length) },
          worker,
        ),
      )
      if (failure) throw failure
      for (
        let offset = 0;
        offset < committedIds.length;
        offset += this.stagedCleanupBatchSize
      ) {
        const ids = committedIds.slice(
          offset,
          offset + this.stagedCleanupBatchSize,
        )
        await this.cleanupStagedRepayments(ids, headers, signal)
        completed += ids.length
        reportProgress()
      }
      // A request can time out after PostgREST has applied it, and staging
      // deletes are soft deletes. Reconcile the final state so a successful
      // commit never leaves an active staged repayment or a deleted/missing
      // repayment in the live table.
      await this.reconcileStagedRepaymentCommit(
        repaymentsToSave,
        headers,
        signal,
      )
    } catch (error) {
      if (!this.isAbortError(error)) {
        throw error
      }
    } finally {
      this.clearRequest('stageRepayments', controller)
    }
  }

  private async commitStagedRepayments(
    repayments: Repayments,
    headers: Record<string, string>,
    signal: AbortSignal,
  ): Promise<void> {
    if (repayments.some(({ id }) => !id)) {
      throw new Error('Cannot commit staged repayments without IDs')
    }
    const normalized = this.normalizeBulkInsertPayload(
      repayments.map((repayment) => ({
        ...repayment,
        updated_by: this.authService.getAuditUser(),
      })),
    )
    const commitResponse = await fetch(`${this.postgrestBaseUrl}/repayments`, {
      method: 'POST',
      headers: {
        ...headers,
        // A timeout may occur after PostgREST has committed the insert. Ignore
        // a shared primary-key conflict so retrying only clears the staging row.
        Prefer: 'resolution=ignore-duplicates,return=minimal',
      },
      body: JSON.stringify(normalized),
      signal,
    })
    if (!commitResponse.ok) {
      throw new Error(
        `Failed to commit repayments: ${commitResponse.status} ${commitResponse.statusText}`,
      )
    }
  }

  private isCardOneCommit(repayments: Repayments): boolean {
    return (
      repayments.length > 0 &&
      repayments.every(
        (repayment) =>
          repayment.data_source?.trim().toLowerCase() === 'cardone',
      )
    )
  }

  private async cleanupStagedRepayments(
    ids: string[],
    headers: Record<string, string>,
    signal: AbortSignal,
  ): Promise<void> {
    if (!ids.length) return
    const cleanupResponse = await fetch(
      `${this.postgrestBaseUrl}/repayments_staging?id=${encodeURIComponent(`in.(${ids.join(',')})`)}&deleted=eq.false`,
      { method: 'DELETE', headers, signal },
    )
    if (!cleanupResponse.ok) {
      throw new Error(
        `Repayments were committed but staging cleanup failed: ${cleanupResponse.status} ${cleanupResponse.statusText}`,
      )
    }
  }

  private async reconcileStagedRepaymentCommit(
    stagedRepayments: Repayments,
    headers: Record<string, string>,
    signal: AbortSignal,
  ): Promise<void> {
    for (
      let offset = 0;
      offset < stagedRepayments.length;
      offset += this.stagedCleanupBatchSize
    ) {
      const batch = stagedRepayments.slice(
        offset,
        offset + this.stagedCleanupBatchSize,
      )
      const ids = batch.map(({ id }) => id).filter((id): id is string => !!id)
      if (!ids.length) continue

      const committed = await this.getRepaymentCommitState(
        'repayments',
        ids,
        headers,
        signal,
      )
      const missingOrDeleted = batch.filter(
        ({ id }) => !id || committed.get(id) !== false,
      )

      if (missingOrDeleted.length) {
        await this.commitStagedRepayments(missingOrDeleted, headers, signal)
      }

      const repaired = await this.getRepaymentCommitState(
        'repayments',
        ids,
        headers,
        signal,
      )
      const softDeletedIds = ids.filter((id) => repaired.get(id) === true)
      if (softDeletedIds.length) {
        const restoreResponse = await fetch(
          `${this.postgrestBaseUrl}/repayments?id=${encodeURIComponent(`in.(${softDeletedIds.join(',')})`)}`,
          {
            method: 'PATCH',
            headers,
            body: JSON.stringify({
              deleted: false,
              updated_by: this.authService.getAuditUser(),
              last_changed_in: 'AWS',
            }),
            signal,
          },
        )
        if (!restoreResponse.ok) {
          throw new Error(
            `Failed to restore committed repayments: ${restoreResponse.status} ${restoreResponse.statusText}`,
          )
        }
      }

      const verified = await this.getRepaymentCommitState(
        'repayments',
        ids,
        headers,
        signal,
      )
      const uncommittedIds = ids.filter((id) => verified.get(id) !== false)
      if (uncommittedIds.length) {
        throw new Error(
          `Unable to commit all staged repayments: ${uncommittedIds.join(', ')}`,
        )
      }

      const staging = await this.getRepaymentCommitState(
        'repayments_staging',
        ids,
        headers,
        signal,
      )
      const activeStagingIds = ids.filter((id) => staging.get(id) === false)
      if (activeStagingIds.length) {
        await this.cleanupStagedRepayments(activeStagingIds, headers, signal)
      }

      const cleaned = await this.getRepaymentCommitState(
        'repayments_staging',
        ids,
        headers,
        signal,
      )
      const unclearedIds = ids.filter((id) => cleaned.get(id) === false)
      if (unclearedIds.length) {
        throw new Error(
          `Unable to clear all staged repayments: ${unclearedIds.join(', ')}`,
        )
      }
    }
  }

  private async getRepaymentCommitState(
    table: RepaymentsTableName,
    ids: string[],
    headers: Record<string, string>,
    signal: AbortSignal,
  ): Promise<Map<string, boolean>> {
    const response = await fetch(
      `${this.postgrestBaseUrl}/${table}?select=id,deleted&id=${encodeURIComponent(`in.(${ids.join(',')})`)}`,
      { method: 'GET', headers, signal },
    )
    if (!response.ok) {
      throw new Error(
        `Failed to verify ${table}: ${response.status} ${response.statusText}`,
      )
    }
    const rows: Array<{ id?: string; deleted?: boolean }> =
      await response.json()
    return new Map(
      (Array.isArray(rows) ? rows : [])
        .filter((row): row is { id: string; deleted?: boolean } => !!row.id)
        .map((row) => [row.id, row.deleted === true]),
    )
  }

  public async validateStagedRepayments(
    fileID: string,
    options?: RequestOptions,
  ): Promise<FileRepaymentValidationResult> {
    const { controller, signal } = this.createRequestContext(
      'validateStagedRepayments',
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const response = await fetch(
        `${this.postgrestBaseUrl}/repayments_staging?select=row_number,description,merchant_id,terminal_id,cash_advance_id,repayment_date,payment_amount,data_source,deleted&source_file=eq.${encodeURIComponent(fileID)}&deleted=eq.false`,
        {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}`, Range: '0-999999' },
          signal,
        },
      )
      if (!response.ok) {
        throw new Error(`Unable to validate repayments: ${response.status}`)
      }
      const repayments: Repayments = await response.json()
      // Deleted staging rows remain in the file for audit/history, but must
      // never block committing the active rows that remain.
      const errors = (repayments ?? [])
        .filter((repayment) => !repayment.deleted)
        .flatMap((repayment) => {
          const missing: string[] = []
          if (!repayment.description?.trim()) missing.push('Description')
          if (
            options?.requireMerchant !== false &&
            !repayment.merchant_id?.trim()
          ) {
            missing.push('Merchant')
          }
          if (
            isTerminalRequired(repayment.description, repayment.data_source) &&
            !repayment.terminal_id?.trim()
          ) {
            missing.push('Terminal')
          }
          if (!repayment.cash_advance_id?.trim()) missing.push('Cash Advance')
          if (!repayment.repayment_date?.toString().trim())
            missing.push('Repayment Date')
          if (
            repayment.payment_amount === null ||
            repayment.payment_amount === undefined ||
            repayment.payment_amount === ''
          ) {
            missing.push('Amount')
          }
          if (!repayment.data_source?.toString().trim())
            missing.push('Data Source')
          return missing.length
            ? [`Row ${repayment.row_number ?? 'unknown'}: ${missing.join(', ')}`]
            : []
        })
      return { valid: errors.length === 0, errors }
    } finally {
      this.clearRequest('validateStagedRepayments', controller)
    }
  }

  async getDuplicates() {}

  public loadMoreRepayments(options?: RequestOptions, query?): Promise<any> {
    return this.loadAllRepayments(options).catch((error) => {
      console.error('Error loading more repayments:', error)
    })
  }

  public async fetchRepaymentsWithQuery(
    queryString: string,
    options?:
      | (RequestOptions & {
          skipCacheUpdate?: boolean
          skipRelationPopulation?: boolean
          excludeDeletedStaging?: boolean
          committedSourceFile?: boolean
        })
      | undefined,
  ): Promise<Repayments> {
    this.logRapidCalls('fetchRepaymentsWithQuery')
    const key = this.createUniqueRequestKey(
      `fetchRepaymentsWithQuery-${queryString}`,
    )
    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    this.loading.next(true)

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const queryConfig = this.parseRepaymentsQueryString(queryString)

      const repayments = await this.fetchRepaymentsFromSources({
        token,
        signal,
        limit: queryConfig.limit,
        offset: queryConfig.offset,
        orderClauses: queryConfig.orderClauses,
        orderString: queryConfig.orderString,
        baseParams: queryConfig.baseParams,
        deletedFilters: queryConfig.deletedFilters,
        skipRelationPopulation: options?.skipRelationPopulation ?? false,
        excludeDeletedStaging: options?.excludeDeletedStaging ?? false,
        committedSourceFile: options?.committedSourceFile ?? false,
      })

      if (!(options?.skipCacheUpdate ?? false)) {
        this.updateRepaymentCache(repayments)
      }

      return repayments
    } catch (error) {
      throw error
    } finally {
      this.loading.next(false)
      this.clearRequest(key, controller)
    }
  }

  /**
   * Returns daily, per-source totals directly from Postgres. Keeping the
   * aggregation in the database avoids loading every individual repayment
   * into the browser for the Reports view.
   */
  public async fetchDailyRepaymentAggregates(
    dateField: 'repayment_date' | 'created_date',
    startDate: string,
    endDate: string,
    options?: RequestOptions,
  ): Promise<RepaymentDailyAggregate[]> {
    const key = this.createUniqueRequestKey(
      `fetchDailyRepaymentAggregates-${dateField}-${startDate}-${endDate}`,
    )
    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const params = new URLSearchParams({
        select: `${dateField},data_source,repayment_sum:payment_amount.sum(),repayment_count:id.count()`,
        [dateField]: `gte.${startDate}`,
        deleted: 'eq.false',
        // Match the report definition exactly: only the Adjustment record
        // type is excluded (and null descriptions follow SQL's <> behavior).
        description: 'neq.Adjustment',
      })
      // Keep this range sargable so the report's date indexes can be used.
      // The report component orders the small aggregate result in the browser.
      params.set('and', `(${dateField}.lt.${endDate})`)

      const response = await this.fetchWithPostgrestAuthRetry(
        `${this.postgrestBaseUrl}/repayments?${params.toString()}`,
        { method: 'GET', signal },
        { token, context: 'loading daily repayment report aggregates' },
      )
      if (!response.ok) {
        throw new Error(
          `Failed to load repayment report aggregates: ${response.status} ${response.statusText}`,
        )
      }

      const payload = await response.json()
      return Array.isArray(payload) ? payload : []
    } finally {
      this.clearRequest(key, controller)
    }
  }

  /**
   * Loads one fixed-size page for a record-level Operations report.
   * Keeping this paged prevents the report from loading every repayment
   * before the user can begin reviewing it.
   */
  public async fetchOperationsRepayments(
    dataSources: string[],
    offset: number,
    limit: number,
    options?: RequestOptions & { orderByRepaymentDate?: boolean },
  ): Promise<OperationsRepaymentPage> {
    const normalizedSources = Array.from(new Set(dataSources.map((source) => source.trim()).filter(Boolean)))
    if (!normalizedSources.length) return { repayments: [] }
    const order = options?.orderByRepaymentDate ? 'repayment_date.desc,id.desc' : 'created_date.desc,id.desc'
    const key = this.createUniqueRequestKey(
      `fetchOperationsRepayments-${normalizedSources.join(',')}-${order}-${offset}-${limit}`,
    )
    const { controller, signal } = this.createRequestContext(key, options?.signal)

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const params = new URLSearchParams({
        select: this.repaymentsSelectFields.join(','),
        data_source: `in.(${normalizedSources.join(',')})`,
        deleted: 'eq.false',
        description: 'neq.Adjustment',
        order,
        limit: String(limit),
        offset: String(offset),
      })
      const response = await this.fetchWithPostgrestAuthRetry(
        `${this.postgrestBaseUrl}/repayments?${params.toString()}`,
        {
          method: 'GET',
          signal,
        },
        { token, context: 'loading Operations repayments' },
      )
      if (!response.ok) {
        throw new Error(
          `Failed to load Operations repayments: ${response.status} ${response.statusText}`,
        )
      }
      const payload = await response.json()
      return { repayments: Array.isArray(payload) ? payload : [] }
    } finally {
      this.clearRequest(key, controller)
    }
  }

  /** Loads Operations report totals separately so record pages can render without waiting for a full aggregation. */
  public async fetchOperationsRepaymentSummary(
    dataSources: string[],
    options?: RequestOptions,
  ): Promise<{ totalRecords: number; totalPaymentAmount: number }> {
    const normalizedSources = Array.from(new Set(dataSources.map((source) => source.trim()).filter(Boolean)))
    if (!normalizedSources.length) return { totalRecords: 0, totalPaymentAmount: 0 }
    const key = this.createUniqueRequestKey(
      `fetchOperationsRepaymentSummary-${normalizedSources.join(',')}`,
    )
    const { controller, signal } = this.createRequestContext(key, options?.signal)

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const params = new URLSearchParams({
        select: 'total_payment_amount:payment_amount.sum(),total_records:id.count()',
        data_source: `in.(${normalizedSources.join(',')})`,
        deleted: 'eq.false',
        description: 'neq.Adjustment',
      })
      const response = await this.fetchWithPostgrestAuthRetry(
        `${this.postgrestBaseUrl}/repayments?${params.toString()}`,
        { method: 'GET', signal },
        { token, context: 'loading Operations repayment totals' },
      )
      if (!response.ok) {
        throw new Error(
          `Failed to load Operations repayment totals: ${response.status} ${response.statusText}`,
        )
      }
      const payload = await response.json()
      return {
        totalPaymentAmount: Array.isArray(payload) ? Number(payload[0]?.total_payment_amount) || 0 : 0,
        totalRecords: Array.isArray(payload) ? Number(payload[0]?.total_records) || 0 : 0,
      }
    } finally {
      this.clearRequest(key, controller)
    }
  }

  /** Loads database-aggregated rows contributing to one Internal Recoveries value. */
  public async fetchLegalInternalRecoveryRepayments(
    repaymentMonth: string,
    dataSources: string[],
    options?: RequestOptions,
  ): Promise<LegalInternalRecoveryContribution[]> {
    const normalizedSources = Array.from(new Set(dataSources.map((source) => source.trim()).filter(Boolean)))
    if (!/^\d{4}-\d{2}$/.test(repaymentMonth) || !normalizedSources.length) return []
    const key = this.createUniqueRequestKey(
      `fetchLegalInternalRecoveryRepayments-${repaymentMonth}-${normalizedSources.join(',')}`,
    )
    const { controller, signal } = this.createRequestContext(key, options?.signal)
    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const rows: LegalInternalRecoveryContribution[] = []
      const pageSize = 1000
      const monthValue = `${repaymentMonth.slice(0, 4)}${Number(repaymentMonth.slice(5))}`
      for (let offset = 0; ; offset += pageSize) {
        const params = new URLSearchParams({
          select: 'repayment_date,created_date,merchants(name),cash_advances(name),payment_sum:payment_amount.sum()',
          repayment_month: `eq.${monthValue}`,
          data_source: `in.(${normalizedSources.join(',')})`,
          deleted: 'eq.false',
          description: 'neq.Adjustment',
          order: 'repayment_date.desc',
          limit: String(pageSize),
          offset: String(offset),
        })
        const response = await this.fetchWithPostgrestAuthRetry(
          `${this.postgrestBaseUrl}/repayments?${params.toString()}`,
          { method: 'GET', signal },
          { token, context: 'loading Internal Recovery contributing repayments' },
        )
        if (!response.ok) {
          throw new Error(
            `Failed to load Internal Recovery repayments: ${response.status} ${response.statusText}`,
          )
        }
        const page = await response.json()
        const normalizedPage = Array.isArray(page) ? page as LegalInternalRecoveryContribution[] : []
        rows.push(...normalizedPage)
        if (normalizedPage.length < pageSize) break
      }
      return rows
    } finally {
      this.clearRequest(key, controller)
    }
  }

  /**
   * Returns per-repayment-date, per-created-date and per-source totals for
   * the grouped repayment reports. Including both dates lets the report show
   * each repayment date once while nesting its created-date aggregates below.
   */
  public async fetchRepaymentReportAggregates(
    startCreatedDate: string,
    endCreatedDate: string,
    options?: RequestOptions,
  ): Promise<RepaymentDailyAggregate[]> {
    const key = this.createUniqueRequestKey(
      `fetchRepaymentReportAggregates-${startCreatedDate}-${endCreatedDate}`,
    )
    const { controller, signal } = this.createRequestContext(key, options?.signal)

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const response = await this.fetchWithPostgrestAuthRetry(
        `${this.postgrestBaseUrl}/rpc/get_created_day_repayment_report`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ p_start_date: startCreatedDate, p_end_date: endCreatedDate }),
          signal,
        },
        { token, context: 'loading grouped repayment report aggregates' },
      )
      if (!response.ok) {
        throw new Error(
          `Failed to load repayment report aggregates: ${response.status} ${response.statusText}`,
        )
      }
      const payload = await response.json()
      return Array.isArray(payload) ? payload : []
    } finally {
      this.clearRequest(key, controller)
    }
  }

  /**
   * The stored repayment_month value is already month-level, so PostgREST
   * returns only month/source aggregates instead of timestamp-level rows.
   */
  public async fetchMonthlyRepaymentAggregates(
    dataSources?: string[],
    options?: RequestOptions,
  ): Promise<RepaymentDailyAggregate[]> {
    const key = this.createUniqueRequestKey(`fetchMonthlyRepaymentAggregates-${dataSources?.join(',') ?? 'all'}`)
    const { controller, signal } = this.createRequestContext(key, options?.signal)

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const baseParams = new URLSearchParams({
        select: 'repayment_month,data_source,repayment_sum:payment_amount.sum(),repayment_count:id.count()',
        repayment_month: 'not.is.null',
        deleted: 'eq.false',
        description: 'neq.Adjustment',
      })
      if (dataSources?.length) baseParams.set('data_source', `in.(${dataSources.join(',')})`)
      const aggregates: RepaymentDailyAggregate[] = []
      const pageSize = 1000
      for (let offset = 0; ; offset += pageSize) {
        const params = new URLSearchParams(baseParams)
        params.set('limit', String(pageSize))
        params.set('offset', String(offset))
        const response = await this.fetchWithPostgrestAuthRetry(
          `${this.postgrestBaseUrl}/repayments?${params.toString()}`,
          { method: 'GET', signal },
          { token, context: 'loading monthly repayment report aggregates' },
        )
        if (!response.ok) {
          throw new Error(
            `Failed to load monthly repayment report aggregates: ${response.status} ${response.statusText}`,
          )
        }
        const payload = await response.json()
        const page = Array.isArray(payload) ? payload : []
        aggregates.push(...page)
        if (page.length < pageSize) break
      }
      return aggregates
    } finally {
      this.clearRequest(key, controller)
    }
  }

  /**
   * Loads the compact repayment dataset used by the Merchant Report. The
   * report groups values by calendar month, cash advance and terminal in the
   * browser because PostgREST cannot group a date column by month directly.
   */
  public async fetchAllSalesRepayments(
    merchantIds?: string[],
    options?: RequestOptions,
  ): Promise<AllSalesRepayment[]> {
    const normalizedMerchantIds = Array.from(
      new Set((merchantIds ?? []).map((id) => String(id).trim()).filter(Boolean)),
    )
    if (normalizedMerchantIds.length === 0) {
      return []
    }
    const key = this.createUniqueRequestKey(
      `fetchAllSalesRepayments-${normalizedMerchantIds.join(',')}`,
    )
    const { controller, signal } = this.createRequestContext(key, options?.signal)

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const rows: AllSalesRepayment[] = []
      const pageSize = 1000
      let offset = 0

      while (true) {
        const params = new URLSearchParams({
          select: 'id,merchant_id,cash_advance_id,terminal_id,repayment_date,payment_amount,card_transactions,merchants(id,name),cash_advances(id,name),terminals(id,name)',
          deleted: 'eq.false',
          description: 'neq.Adjustment',
          repayment_date: 'not.is.null',
          order: 'repayment_date.desc,id.asc',
          limit: String(pageSize),
          offset: String(offset),
        })

        params.set('merchant_id', `in.(${normalizedMerchantIds.join(',')})`)

        const response = await this.fetchWithPostgrestAuthRetry(
          `${this.postgrestBaseUrl}/repayments?${params.toString()}`,
          { method: 'GET', signal },
          { token, context: 'loading merchant report repayments' },
        )
        if (!response.ok) {
          throw new Error(
            `Failed to load merchant report repayments: ${response.status} ${response.statusText}`,
          )
        }

        const page = (await response.json()) as AllSalesRepayment[]
        rows.push(...(Array.isArray(page) ? page : []))
        if (!Array.isArray(page) || page.length < pageSize) break
        offset += page.length
      }

      return rows
    } finally {
      this.clearRequest(key, controller)
    }
  }

  /** Loads the repayments contributing to a selected Merchant Report table value. */
  public async fetchAllSalesCellRepayments(
    filter: AllSalesCellFilter,
    options?: RequestOptions,
  ): Promise<Repayments> {
    const [year, month] = filter.month.split('-').map(Number)
    const start = `${filter.month}-01`
    const end = new Date(Date.UTC(year, month, 1)).toISOString().slice(0, 10)
    const key = this.createUniqueRequestKey(
      `fetchAllSalesCellRepayments-${filter.merchantId ?? ''}-${filter.cashAdvanceId ?? ''}-${filter.terminalId ?? ''}-${filter.month}`,
    )
    const { controller, signal } = this.createRequestContext(key, options?.signal)

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const repaymentIds = Array.from(
        new Set((filter.repaymentIds ?? []).map(String).filter(Boolean)),
      )
      const baseParams = {
        deleted: 'eq.false',
        description: 'neq.Adjustment',
        order: 'repayment_date.asc,id.asc',
      }

      if (repaymentIds.length) {
        const rows: Repayments = []
        for (let index = 0; index < repaymentIds.length; index += 100) {
          const params = new URLSearchParams(baseParams)
          params.set('id', `in.(${repaymentIds.slice(index, index + 100).join(',')})`)
          rows.push(
            ...(await this.fetchRepaymentTable(
              'repayments',
              params.toString(),
              token,
              signal,
            )),
          )
        }
        return this.normalizeRepayments(rows, false)
      }

      const params = new URLSearchParams({
        ...baseParams,
        and: `(repayment_date.gte.${start},repayment_date.lt.${end})`,
      })
      if (filter.merchantId) params.set('merchant_id', `eq.${filter.merchantId}`)
      if (filter.filterByCashAdvance) {
        params.set(
          'cash_advance_id',
          filter.cashAdvanceId ? `eq.${filter.cashAdvanceId}` : 'is.null',
        )
      }
      if (filter.filterByTerminal) {
        params.set(
          'terminal_id',
          filter.terminalId ? `eq.${filter.terminalId}` : 'is.null',
        )
      }

      const rows = await this.fetchRepaymentTable('repayments', params.toString(), token, signal)
      return this.normalizeRepayments(rows, false)
    } finally {
      this.clearRequest(key, controller)
    }
  }

  /** Loads the committed repayments contributing to a selected report cell. */
  public async fetchReportCellRepayments(
    dateField: 'repayment_date' | 'created_date',
    date: string,
    dataSources: string[],
    options?: RequestOptions,
  ): Promise<Repayments> {
    const key = this.createUniqueRequestKey(
      `fetchReportCellRepayments-${dateField}-${date}-${dataSources.join(',')}`,
    )
    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const rows: Repayments = []
      const pageSize = 1000
      let offset = 0

      while (true) {
        const params = new URLSearchParams({
          [dateField]: `eq.${date}`,
          data_source: `in.(${dataSources.join(',')})`,
          deleted: 'eq.false',
          description: 'neq.Adjustment',
          limit: String(pageSize),
          offset: String(offset),
          order: 'repayment_date.asc,id.asc',
        })
        const page = await this.fetchRepaymentTable(
          'repayments',
          params.toString(),
          token,
          signal,
        )
        rows.push(...page)
        if (page.length < pageSize) break
        offset += page.length
      }

      return this.normalizeRepayments(rows, false)
    } finally {
      this.clearRequest(key, controller)
    }
  }

  /** Loads the records behind a created-day/repayment-month aggregate cell. */
  public async fetchGroupedReportCellRepayments(
    filter: {
      dataSources: string[]
      repaymentDate?: string
      createdDate?: string
      repaymentMonth?: string
      includeUnspecified?: boolean
    },
    options?: RequestOptions,
  ): Promise<Repayments> {
    const key = this.createUniqueRequestKey(`fetchGroupedReportCellRepayments-${filter.repaymentDate ?? filter.repaymentMonth}-${filter.createdDate ?? ''}-${filter.dataSources.join(',')}`)
    const { controller, signal } = this.createRequestContext(key, options?.signal)
    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const baseParams = new URLSearchParams({
        deleted: 'eq.false',
        description: 'neq.Adjustment',
        order: 'repayment_date.asc,id.asc',
      })
      baseParams.set(
        'data_source',
        filter.includeUnspecified ? 'is.null' : `in.(${filter.dataSources.join(',')})`,
      )
      const conditions: string[] = []
      if (filter.repaymentDate) conditions.push(`repayment_date.eq.${filter.repaymentDate}`)
      if (filter.createdDate) {
        const end = new Date(`${filter.createdDate}T00:00:00Z`)
        end.setUTCDate(end.getUTCDate() + 1)
        baseParams.set('created_date', `gte.${filter.createdDate}`)
        conditions.push(`created_date.lt.${end.toISOString().slice(0, 10)}`)
      }
      if (filter.repaymentMonth) {
        const [year, month] = filter.repaymentMonth.split('-').map(Number)
        const end = new Date(Date.UTC(year, month, 1)).toISOString().slice(0, 10)
        conditions.push(`repayment_date.gte.${filter.repaymentMonth}-01`, `repayment_date.lt.${end}`)
      }
      if (conditions.length) baseParams.set('and', `(${conditions.join(',')})`)
      const rows: Repayments = []
      const pageSize = 1000
      const fetchPage = async (offset: number): Promise<Repayments> => {
        const params = new URLSearchParams(baseParams)
        params.set('limit', String(pageSize))
        params.set('offset', String(offset))
        return this.fetchRepaymentTable('repayments', params.toString(), token, signal)
      }

      const firstPage = await fetchPage(0)
      rows.push(...firstPage)
      if (firstPage.length === pageSize) {
        // Month/source cells can contain many thousands of repayments. Load
        // later pages concurrently, rather than making the UI wait for each
        // 1,000-row request to finish before starting the next one.
        const concurrentPageCount = 4
        for (let offset = pageSize; ; offset += pageSize * concurrentPageCount) {
          const pages = await Promise.all(
            Array.from({ length: concurrentPageCount }, (_, index) =>
              fetchPage(offset + index * pageSize),
            ),
          )
          rows.push(...pages.flat())
          if (pages.some((page) => page.length < pageSize)) break
        }
      }
      return this.normalizeRepayments(rows, false)
    } finally {
      this.clearRequest(key, controller)
    }
  }

  public async fetchRepaymentsCountWithQuery(
    queryString: string,
    options?: RequestOptions & {
      excludeDeletedStaging?: boolean
      committedSourceFile?: boolean
    },
  ): Promise<number> {
    this.logRapidCalls('fetchRepaymentsCountWithQuery')
    const key = this.createUniqueRequestKey(
      `fetchRepaymentsCountWithQuery-${queryString}`,
    )
    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const queryConfig = this.parseRepaymentsQueryString(queryString)

      return await this.countRepaymentsFromSources({
        token,
        signal,
        orderClauses: queryConfig.orderClauses,
        baseParams: queryConfig.baseParams,
        deletedFilters: queryConfig.deletedFilters,
        excludeDeletedStaging: options?.excludeDeletedStaging ?? false,
        committedSourceFile: options?.committedSourceFile ?? false,
      })
    } catch (error) {
      throw error
    } finally {
      this.clearRequest(key, controller)
    }
  }

  public async loadMerchantContextRepaymentRelations(
    merchantName: string,
    options?: RequestOptions,
  ): Promise<Repayments> {
    const normalizedMerchantName = merchantName.trim()
    if (!normalizedMerchantName) {
      return []
    }

    const key = `loadMerchantContextRepaymentRelations:${normalizedMerchantName}`
    const { controller, signal } = this.createRequestContext(
      key,
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const limit = 500
      let offset = 0
      const repayments: Repayments = []

      while (true) {
        const params = new URLSearchParams()
        params.set(
          'select',
          'id,merchant_id,cash_advance_id,terminal_id,merchants(name,id),terminals(name,id),cash_advances(name,id)',
        )
        params.set('order', 'repayment_date.desc,id.asc')
        params.set('limit', String(limit))
        params.set('offset', String(offset))
        params.set('deleted', 'eq.false')
        params.set('merchants.name', `eq.${normalizedMerchantName}`)
        params.set('merchants', 'not.is.null')

        const baseParams = this.buildBaseQueryParams()
        Object.entries(baseParams).forEach(([paramKey, value]) => {
          if (value) {
            params.set(paramKey, value)
          }
        })

        const response = await fetch(
          `${this.postgrestBaseUrl}/${this.repaymentsSource}?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal,
          },
        )

        if (!response.ok) {
          throw new Error(
            `Failed to load merchant context repayment relations: ${response.status} ${response.statusText}`,
          )
        }

        const pageItems: Repayments = (await response.json()) ?? []
        repayments.push(...pageItems)

        if (pageItems.length < limit) {
          break
        }

        offset += pageItems.length
      }

      return repayments
    } catch (error) {
      if (this.isAbortError(error)) {
        return []
      }
      throw error
    } finally {
      this.clearRequest(key, controller)
    }
  }

  private async populateRepaymentRelations(
    repayments: Repayments,
    token: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!Array.isArray(repayments) || repayments.length === 0) {
      return
    }

    const [merchantIds, terminalIds, cashAdvanceIds] = [
      this.collectRelationIds(repayments, 'merchant_id'),
      this.collectRelationIds(repayments, 'terminal_id'),
      this.collectRelationIds(repayments, 'cash_advance_id'),
    ]

    const [merchants, terminals, cashAdvances] = await Promise.all([
      this.fetchEntityMap('merchants', merchantIds, token, signal),
      this.fetchEntityMap('terminals', terminalIds, token, signal),
      this.fetchEntityMap('cash_advances', cashAdvanceIds, token, signal),
    ])

    repayments.forEach((repayment) => {
      if (repayment.merchant_id) {
        const merchant = merchants.get(repayment.merchant_id)
        if (merchant) {
          repayment.merchants = {
            id: merchant.id,
            name: merchant.name ?? '',
          }
        }
      }

      if (repayment.terminal_id) {
        const terminal = terminals.get(repayment.terminal_id)
        if (terminal) {
          repayment.terminals = {
            id: terminal.id,
            name: terminal.name ?? '',
          }
        }
      }

      if (repayment.cash_advance_id) {
        const cashAdvance = cashAdvances.get(repayment.cash_advance_id)
        if (cashAdvance) {
          repayment.cash_advances = {
            id: cashAdvance.id,
            name: cashAdvance.name ?? '',
          }
        }
      }
    })

    this.relationsPopulated = true
  }

  private parseRepaymentsQueryString(queryString: string) {
    const params = new URLSearchParams(queryString ?? '')
    const limit = this.parseNumericParam(params.get('limit'), 100)
    const offset = this.parseNumericParam(params.get('offset'), 0)
    const orderClauses = this.extractOrderClauses(params.getAll('order'))
    const orderString = orderClauses.join(',')
    const deletedFilters = this.parseDeletedFilters(params.getAll('deleted'))

    params.delete('limit')
    params.delete('offset')
    params.delete('order')
    // Keep the deleted constraint in the query sent to PostgREST. It is also
    // parsed above for the merged-source count/client fallback, but removing it
    // here meant pagination occurred before deleted rows were excluded.

    const baseParams = new URLSearchParams()
    params.forEach((value, key) => {
      baseParams.append(key, value)
    })

    return {
      limit,
      offset,
      orderClauses,
      orderString,
      baseParams,
      deletedFilters,
    }
  }

  private parseNumericParam(value: string | null, fallback: number): number {
    if (value === null || value === undefined) {
      return fallback
    }
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback
  }

  private extractOrderClauses(values: string[]): string[] {
    const clauses: string[] = []
    values.forEach((entry) => {
      entry
        .split(',')
        .map((clause) => clause.trim())
        .filter(Boolean)
        .forEach((clause) => clauses.push(clause))
    })

    if (!clauses.length) {
      clauses.push('repayment_date.desc')
    }

    return clauses
  }

  private parseDeletedFilters(entries: string[]): DeletedFilter[] {
    return entries
      .map((entry) => {
        const [operator, rawValue] = entry.split('.')
        if (!operator || !rawValue) {
          return null
        }
        if (operator !== 'eq' && operator !== 'neq') {
          return null
        }
        const decoded = decodeURIComponent(rawValue)
        if (decoded !== 'true' && decoded !== 'false') {
          return null
        }
        return { operator, value: decoded === 'true' }
      })
      .filter((filter): filter is DeletedFilter => !!filter)
  }

  private async fetchRepaymentsFromSources(params: {
    token: string
    signal?: AbortSignal
    limit: number
    offset: number
    orderClauses: string[]
    orderString: string
    baseParams: URLSearchParams
    deletedFilters: DeletedFilter[]
    skipRelationPopulation: boolean
    excludeDeletedStaging: boolean
    committedSourceFile: boolean
  }): Promise<Repayments> {
    this.logRapidCalls('fetchRepaymentsFromSources')
    const { token, signal, limit, offset } = params

    if (limit <= 0) {
      return []
    }

    const hasGlobalSearch = params.baseParams.has('or')
    // A committed file is fully represented by repayments. Its staging rows
    // may be retained as audit history, but must never be queried for the
    // file view (including the Deleted Repayments toggle).
    const queryTables: RepaymentsTableName[] =
      params.committedSourceFile && this.sourceFileId
      ? ['repayments']
      : this.getQueryTables()
    const canUseSearchView =
      hasGlobalSearch &&
      queryTables.length === 1 &&
      queryTables[0] === 'repayments'
    if (canUseSearchView) {
      return this.fetchRepaymentsFromSearchView({
        token,
        signal,
        limit,
        offset,
        orderClauses: params.orderClauses,
        baseParams: params.baseParams,
        deletedFilters: params.deletedFilters,
      })
    }

    const fetchLimit = Math.max(limit + offset, 1)
    const responses = await Promise.all(
      queryTables.map((table) =>
        this.fetchRepaymentTable(
          table,
          this.buildTableQuery(
            params.baseParams,
            fetchLimit,
            params.orderString,
            table,
          ),
          token,
          signal,
        ).then((rows) => ({
          table,
          rows,
        })),
      ),
    )

    // A known committed file never needs the staging reconciliation probe.
    // Besides being redundant, that probe queries repayments_staging.
    const savedFile =
      params.committedSourceFile ||
      this.sourceFileIsCommitted ||
      (this.sourceFileId !== null &&
        (await this.hasAllActiveStagedRepaymentsBeenCommitted(token, signal)))
    const activeRows =
      responses.find(({ table }) => table === 'repayments')?.rows ?? []
    const stagingRows =
      responses.find(({ table }) => table === 'repayments_staging')?.rows ?? []
    let combined = this.dedupeRepaymentsById(
      this.dedupeSavedFileRepayments(
      this.normalizeRepayments(activeRows, false),
      this.normalizeRepayments(stagingRows, true),
      savedFile,
      ),
    )

    combined = this.applyDeletedFilters(combined, params.deletedFilters)
    if (params.excludeDeletedStaging) {
      combined = combined.filter(
        (repayment) => !(repayment.staged && repayment.deleted),
      )
    }
    if (!params.skipRelationPopulation) {
      await this.populateRepaymentRelations(combined, token, signal)
    }
    const sorted = this.sortRepaymentsByClauses(combined, params.orderClauses)
    const startIndex = Math.min(offset, Math.max(sorted.length, 0))
    const endIndex = Math.min(sorted.length, startIndex + limit)
    return sorted.slice(startIndex, endIndex)
  }

  private async countRepaymentsFromSources(params: {
    token: string
    signal?: AbortSignal
    orderClauses: string[]
    baseParams: URLSearchParams
    deletedFilters: DeletedFilter[]
    excludeDeletedStaging: boolean
    committedSourceFile: boolean
  }): Promise<number> {
    this.logRapidCalls('countRepaymentsFromSources')
    const hasGlobalSearch = params.baseParams.has('or')
    const queryTables: RepaymentsTableName[] =
      params.committedSourceFile && this.sourceFileId
      ? ['repayments']
      : this.getQueryTables()
    const canUseSearchView =
      hasGlobalSearch &&
      queryTables.length === 1 &&
      queryTables[0] === 'repayments'

    if (canUseSearchView) {
      return this.countRepaymentsInSearchView(params)
    }

    const orderString = params.orderClauses.join(',')
    const counts = await Promise.all(
      queryTables.flatMap((table) => [
        this.countRepaymentTable(
          table,
          this.buildTableQuery(params.baseParams, 1, orderString, table),
          params.token,
          params.signal,
          false,
        ).then((count) => ({ table, deleted: false, count })),
        this.countRepaymentTable(
          table,
          this.buildTableQuery(params.baseParams, 1, orderString, table),
          params.token,
          params.signal,
          true,
        ).then((count) => ({ table, deleted: true, count })),
      ]),
    )

    const savedFile =
      params.committedSourceFile ||
      this.sourceFileIsCommitted ||
      (this.sourceFileId !== null &&
        (await this.hasAllActiveStagedRepaymentsBeenCommitted(
          params.token,
          params.signal,
        )))

    const activeCount = counts
      .filter(
        (entry) =>
          !entry.deleted &&
          !(savedFile && entry.table === 'repayments_staging'),
      )
      .reduce((sum, entry) => sum + entry.count, 0)
    const deletedCount = counts
      .filter(
        (entry) =>
          entry.deleted &&
          !(savedFile && entry.table === 'repayments_staging') &&
          !(params.excludeDeletedStaging && entry.table === 'repayments_staging'),
      )
      .reduce((sum, entry) => sum + entry.count, 0)

    return this.applyDeletedFiltersToCounts(
      {
        active: activeCount,
        deleted: deletedCount,
      },
      params.deletedFilters,
    )
  }

  private getQueryTables(): RepaymentsTableName[] {
    if (this.sourceFileId) {
      if (this.sourceFileIsCommitted) {
        return ['repayments']
      }
      return ['repayments', 'repayments_staging']
    }
    return [this.repaymentsSource]
  }

  private async countRepaymentsInSearchView(params: {
    token: string
    signal?: AbortSignal
    orderClauses: string[]
    baseParams: URLSearchParams
    deletedFilters: DeletedFilter[]
  }): Promise<number> {
    const mappedBaseParams = this.mapParamsForSearchView(params.baseParams)
    const mappedOrderClauses = this.mapOrderClausesForSearchView(
      params.orderClauses,
    )
    const mappedOrderString = mappedOrderClauses.join(',')
    const query = this.buildTableQuery(mappedBaseParams, 1, mappedOrderString)
    const count = await this.countRepaymentSearchTable(
      query,
      params.token,
      params.signal,
    )

    return this.applyDeletedFiltersToCounts(
      {
        active: count,
        deleted: 0,
      },
      params.deletedFilters,
    )
  }

  private async fetchRepaymentsFromSearchView(params: {
    token: string
    signal?: AbortSignal
    limit: number
    offset: number
    orderClauses: string[]
    baseParams: URLSearchParams
    deletedFilters: DeletedFilter[]
  }): Promise<Repayments> {
    this.logRapidCalls('fetchRepaymentsFromSearchView')
    const { token, signal, limit, offset } = params
    const mappedBaseParams = this.mapParamsForSearchView(params.baseParams)
    const mappedOrderClauses = this.mapOrderClausesForSearchView(
      params.orderClauses,
    )
    const mappedOrderString = mappedOrderClauses.join(',')
    const fetchLimit = Math.max(limit + offset, 1)
    const query = this.buildTableQuery(
      mappedBaseParams,
      fetchLimit,
      mappedOrderString,
    )
    const records = await this.fetchRepaymentSearchTable(query, token, signal)

    const normalized = this.normalizeRepayments(records, false)
    const filtered = this.applyDeletedFilters(normalized, params.deletedFilters)
    await this.populateRepaymentRelations(filtered, token, signal)
    const sorted = this.sortRepaymentsByClauses(filtered, mappedOrderClauses)
    const startIndex = Math.min(offset, Math.max(sorted.length, 0))
    const endIndex = Math.min(sorted.length, startIndex + limit)
    return sorted.slice(startIndex, endIndex)
  }

  private buildTableQuery(
    baseParams: URLSearchParams,
    limit: number,
    orderString: string,
    table?: RepaymentsTableName,
  ): string {
    const params = new URLSearchParams(baseParams.toString())
    params.set('limit', String(limit))
    params.set('offset', '0')
    if (orderString) {
      const tableOrder =
        table === 'repayments_staging'
          ? orderString
              .split(',')
              .filter(
                (clause) => !clause.startsWith('generated_from_repayment_id.'),
              )
              .join(',')
          : orderString
      // This clause is an embedded-table display rank. It is evaluated after
      // fetching so no database schema change is needed for the embed.
      const databaseOrder = tableOrder
        .split(',')
        .filter(
          (clause) => !clause.startsWith('embedded_balance_zero_first.'),
        )
        .join(',')
      if (databaseOrder) {
        params.set('order', databaseOrder)
      } else {
        params.delete('order')
      }
    }
    return params.toString()
  }

  private async countRepaymentTable(
    table: 'repayments' | 'repayments_staging',
    query: string,
    token: string,
    signal?: AbortSignal,
    deleted?: boolean,
  ): Promise<number> {
    const select = encodeURIComponent(
      this.getRepaymentSelectFields(table).join(','),
    )
    const params = new URLSearchParams(query)
    params.set('limit', '1')
    params.set('offset', '0')
    if (deleted !== undefined) {
      params.set('deleted', `eq.${deleted}`)
    }
    const suffix = params.toString() ? `&${params.toString()}` : ''
    const response = await this.fetchWithPostgrestAuthRetry(
      `${this.postgrestBaseUrl}/${table}?select=${select}${suffix}`,
      {
        method: 'HEAD',
        headers: {
          Prefer: 'count=exact',
        },
        signal,
      },
      {
        token,
        context: `counting ${table}`,
      },
    )

    if (!response.ok) {
      throw new Error(
        `Failed to count ${table}: ${response.status} ${response.statusText}`,
      )
    }

    return this.parseTotalFromContentRange(
      response.headers.get('Content-Range'),
    )
  }

  private async fetchRepaymentTable(
    table: 'repayments' | 'repayments_staging',
    query: string,
    token: string,
    signal?: AbortSignal,
  ): Promise<Repayments> {
    const select = encodeURIComponent(
      this.getRepaymentSelectFields(table).join(','),
    )
    const suffix = query ? `&${query}` : ''
    const response = await this.fetchWithPostgrestAuthRetry(
      `${this.postgrestBaseUrl}/${table}?select=${select}${suffix}`,
      {
        method: 'GET',
        signal,
      },
      {
        token,
        context: `loading ${table}`,
      },
    )

    if (!response.ok) {
      throw new Error(
        `Failed to load ${table}: ${response.status} ${response.statusText}`,
      )
    }

    const payload = await response.json()
    return Array.isArray(payload) ? payload : []
  }

  private mapParamsForSearchView(params: URLSearchParams): URLSearchParams {
    const mapped = new URLSearchParams()
    params.forEach((value, key) => {
      if (key === 'or') {
        mapped.append('or', this.mapOrValueForSearchView(value))
        return
      }
      mapped.append(this.mapSearchViewField(key), value)
    })
    return mapped
  }

  private mapOrderClausesForSearchView(clauses: string[]): string[] {
    return clauses.map((clause) => {
      const parts = clause.split('.').filter(Boolean)
      const field = parts.shift()
      if (!field) {
        return clause
      }
      const mappedField = this.mapSearchViewField(field)
      if (!parts.length) {
        return mappedField
      }
      return [mappedField, ...parts].join('.')
    })
  }

  private mapOrValueForSearchView(value: string): string {
    if (!value) {
      return value
    }
    const trimmed = value.replace(/^\(/, '').replace(/\)$/, '')
    const mappedClauses = trimmed
      .split(',')
      .map((clause) => {
        const segments = clause.split('.')
        const field = segments.shift()
        if (!field) {
          return clause
        }
        const mappedField = this.mapSearchViewField(field)
        return [mappedField, ...segments].join('.')
      })
      .join(',')
    return `(${mappedClauses})`
  }

  private mapSearchViewField(field: string): string {
    switch (field) {
      case 'merchants(name)':
      case 'merchants.name':
        return 'merchant_name'
      case 'cash_advances(name)':
      case 'cash_advance(name)':
      case '"cash_advances".name':
      case 'cash_advances.name':
        return 'cash_advance_name'
      case 'terminals(name)':
      case 'terminals.name':
        return 'terminal_name'
      default:
        return field
    }
  }

  private formatDateForPostgrest(
    value: string | Date | undefined | null,
  ): string | undefined {
    if (!value) {
      return undefined
    }

    const toTwo = (num: number) => String(num).padStart(2, '0')

    if (value instanceof Date) {
      return `${value.getFullYear()}-${toTwo(
        value.getMonth() + 1,
      )}-${toTwo(value.getDate())}`
    }

    const trimmed = value.trim()
    if (!trimmed) {
      return undefined
    }

    const slashParts = trimmed.split('/')
    const yearFirstSlashMatch = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/)
    if (yearFirstSlashMatch) {
      const [, year, month, day] = yearFirstSlashMatch
      return `${year}-${toTwo(Number(month))}-${toTwo(Number(day))}`
    }
    if (slashParts.length === 3) {
      const [d, m, y] = slashParts.map((part) => part.trim())
      if (d && m && y) {
        const normalizedYear = y.length === 2 ? `20${y}` : y
        return `${normalizedYear}-${toTwo(Number(m))}-${toTwo(Number(d))}`
      }
    }

    const isoParts = trimmed.split('-')
    if (isoParts.length === 3) {
      const [y, m, d] = isoParts
      const parsed = new Date(Number(y), Number(m) - 1, Number(d))
      if (!Number.isNaN(parsed.getTime())) {
        return this.formatDateForPostgrest(parsed)
      }
    }

    const parsed = new Date(trimmed)
    if (!Number.isNaN(parsed.getTime())) {
      return this.formatDateForPostgrest(parsed)
    }

    return trimmed
  }

  private normalizeRepaymentForPostgrest(repayment: Repayment): Repayment {
    const payload: Repayment = { ...repayment }
    if ('description' in repayment) {
      ;(payload as any).description = this.normalizeNullableTextField(
        repayment.description,
      )
    }
    if ('comments' in repayment) {
      ;(payload as any).comments = this.normalizeNullableTextField(
        repayment.comments,
      )
    }
    if ('data_source' in repayment) {
      ;(payload as any).data_source = this.normalizeDataSource(
        repayment.data_source,
      )
    }
    if ('merchant_id' in repayment) {
      ;(payload as any).merchant_id = this.normalizeNullableTextField(
        repayment.merchant_id,
      )
    }
    if ('cash_advance_id' in repayment) {
      ;(payload as any).cash_advance_id = this.normalizeNullableTextField(
        repayment.cash_advance_id,
      )
    }
    if ('terminal_id' in repayment) {
      ;(payload as any).terminal_id = this.normalizeNullableTextField(
        repayment.terminal_id,
      )
    }
    if ('repayment_date' in repayment) {
      ;(payload as any).repayment_date =
        this.formatDateForPostgrest(repayment.repayment_date) ?? null
    }
    if ('payment_amount' in repayment) {
      ;(payload as any).payment_amount = this.normalizeNullablePaymentAmount(
        repayment.payment_amount,
      )
    }
    if ('x365_split' in repayment) {
      ;(payload as any).x365_split = this.normalize365SplitPercentage(
        repayment.x365_split,
      )
    }
    this.removeEmptyOptionalNumericFields(payload)
    delete (payload as any).cash_advances
    delete (payload as any).merchants
    delete (payload as any).terminals
    delete (payload as any).staged
    delete (payload as any).isNew
    return payload
  }

  /**
   * PostgREST bulk inserts require every JSON object in the array to have the
   * exact same set of keys. Spreadsheet rows naturally omit optional cells,
   * and JSON.stringify omits properties whose value is undefined. Complete
   * the union of the keys with null so optional columns stay nullable while
   * each row has an identical request shape.
   */
  private normalizeBulkInsertPayload(repayments: Repayments): Repayments {
    const normalized = repayments.map((repayment) =>
      this.normalizeRepaymentForPostgrest(repayment),
    )
    const keys = new Set<string>()

    normalized.forEach((repayment) => {
      Object.keys(repayment).forEach((key) => keys.add(key))
    })

    return normalized.map((repayment) => {
      const completePayload: Repayment = { ...repayment }
      keys.forEach((key) => {
        if (!(key in completePayload) || completePayload[key] === undefined) {
          ;(completePayload as Record<string, unknown>)[key] = null
        }
      })
      return completePayload
    })
  }

  private normalizeNullableTextField(
    value: string | undefined | null,
  ): string | null {
    if (typeof value !== 'string') {
      return null
    }

    const trimmed = value.trim()
    return trimmed ? trimmed : null
  }

  private normalizeDataSource(value: string | undefined | null): string | null {
    const normalized = this.normalizeNullableTextField(value)
    return normalized?.toLowerCase() === 'gocardless'
      ? 'Gocardless'
      : normalized
  }

  private normalizeNullablePaymentAmount(
    value: number | string | undefined | null,
  ): number | string | null {
    if (value === undefined || value === null) {
      return null
    }

    if (typeof value === 'string' && !value.trim()) {
      return null
    }

    return value
  }

  private normalize365SplitPercentage(
    value: number | string | undefined | null,
  ): number | string | null {
    const normalized = this.normalizeNullablePaymentAmount(value)
    if (normalized === null) return null
    const numberValue = Number(normalized)
    if (!Number.isFinite(numberValue) || numberValue === 0) return null
    // Spreadsheet percentage cells are read as fractions (0.15 for 15%).
    const percentage = Math.abs(numberValue) < 1 ? numberValue * 100 : numberValue
    return Number(percentage.toFixed(6))
  }

  private removeEmptyOptionalNumericFields(payload: Repayment): void {
    const numericFields: Array<keyof Repayment> = [
      'balance',
      'previous_balance',
      'new_balance',
    ]

    numericFields.forEach((field) => {
      if (payload[field] === '') {
        delete payload[field]
      }
    })
  }

  private async fetchRepaymentSearchTable(
    query: string,
    token: string,
    signal?: AbortSignal,
  ): Promise<Repayments> {
    const select = encodeURIComponent('*')
    const suffix = query ? `&${query}` : ''
    const response = await this.fetchWithPostgrestAuthRetry(
      `${this.postgrestBaseUrl}/${this.repaymentsSearchView}?select=${select}${suffix}`,
      {
        method: 'GET',
        signal,
      },
      {
        token,
        context: `loading ${this.repaymentsSearchView}`,
      },
    )

    if (!response.ok) {
      throw new Error(
        `Failed to load ${this.repaymentsSearchView}: ${response.status} ${response.statusText}`,
      )
    }

    const payload = await response.json()
    return Array.isArray(payload) ? payload : []
  }

  private async countRepaymentSearchTable(
    query: string,
    token: string,
    signal?: AbortSignal,
  ): Promise<number> {
    const select = encodeURIComponent('id')
    const params = new URLSearchParams(query)
    params.set('limit', '1')
    params.set('offset', '0')
    const suffix = params.toString() ? `&${params.toString()}` : ''
    const response = await this.fetchWithPostgrestAuthRetry(
      `${this.postgrestBaseUrl}/${this.repaymentsSearchView}?select=${select}${suffix}`,
      {
        method: 'GET',
        headers: {
          Prefer: 'count=exact',
        },
        signal,
      },
      {
        token,
        context: `counting ${this.repaymentsSearchView}`,
      },
    )

    if (!response.ok) {
      throw new Error(
        `Failed to count ${this.repaymentsSearchView}: ${response.status} ${response.statusText}`,
      )
    }

    return this.parseTotalFromContentRange(
      response.headers.get('Content-Range'),
    )
  }

  private normalizeRepayments(
    records: Repayments,
    staged: boolean,
  ): Repayments {
    if (!Array.isArray(records)) {
      return []
    }
    return records.map((record) => ({
      ...record,
      deleted: !!record.deleted,
      staged,
    }))
  }

  /**
   * A completed file can temporarily retain its staging rows after the same
   * IDs have been committed. Keep staging visible for partial commits, but
   * prefer the committed records once every active staging row is committed.
   */
  private dedupeSavedFileRepayments(
    activeRepayments: Repayments,
    stagingRepayments: Repayments,
    savedFile = false,
  ): Repayments {
    if (!this.sourceFileId || !stagingRepayments.length || !savedFile) {
      return [...activeRepayments, ...stagingRepayments]
    }
    return activeRepayments
  }

  private dedupeRepaymentsById(repayments: Repayments): Repayments {
    const seenIds = new Set<string>()
    return repayments.filter((repayment) => {
      const id = repayment?.id
      if (!id || seenIds.has(id)) {
        return !id
      }
      seenIds.add(id)
      return true
    })
  }

  private async hasAllActiveStagedRepaymentsBeenCommitted(
    token: string,
    signal?: AbortSignal,
  ): Promise<boolean> {
    if (!this.sourceFileId) return false

    const [activeIds, stagingIds] = await Promise.all([
      this.fetchActiveRepaymentIdsForFile('repayments', token, signal),
      this.fetchActiveRepaymentIdsForFile('repayments_staging', token, signal),
    ])
    return (
      stagingIds.size > 0 && [...stagingIds].every((id) => activeIds.has(id))
    )
  }

  private async fetchActiveRepaymentIdsForFile(
    table: RepaymentsTableName,
    token: string,
    signal?: AbortSignal,
  ): Promise<Set<string>> {
    const pageSize = 1000
    const ids = new Set<string>()
    let offset = 0

    while (true) {
      const params = new URLSearchParams({
        select: 'id',
        source_file: `eq.${this.sourceFileId}`,
        deleted: 'eq.false',
        limit: String(pageSize),
        offset: String(offset),
      })
      const response = await this.fetchWithPostgrestAuthRetry(
        `${this.postgrestBaseUrl}/${table}?${params.toString()}`,
        { method: 'GET', signal },
        { token, context: `checking committed ${table} repayments` },
      )
      if (!response.ok) {
        throw new Error(
          `Failed to check ${table} repayments: ${response.status} ${response.statusText}`,
        )
      }

      const rows: Array<{ id?: string }> = (await response.json()) ?? []
      rows.forEach((row) => row.id && ids.add(row.id))
      if (rows.length < pageSize) break
      offset += rows.length
    }

    return ids
  }

  private resolveRepaymentMutationTable(
    repayment: Repayment | null | undefined,
  ): RepaymentsTableName | null {
    if (repayment?.staged === true) {
      return 'repayments_staging'
    }

    if (!this.sourceFileId && this.repaymentsSource === 'repayments_staging') {
      return 'repayments_staging'
    }

    if (repayment?.staged === false) {
      return 'repayments'
    }

    if (!this.sourceFileId && this.repaymentsSource === 'repayments') {
      return 'repayments'
    }

    return null
  }

  private applyDeletedFilters(
    repayments: Repayments,
    filters: DeletedFilter[],
  ): Repayments {
    if (!filters.length) {
      return repayments
    }

    return repayments.filter((repayment) => {
      const value = !!repayment.deleted
      return filters.every((filter) =>
        filter.operator === 'eq'
          ? value === filter.value
          : value !== filter.value,
      )
    })
  }

  private applyDeletedFiltersToCounts(
    counts: { active: number; deleted: number },
    filters: DeletedFilter[],
  ): number {
    const includeDeleted = this.passesDeletedFilters(true, filters)
    const includeActive = this.passesDeletedFilters(false, filters)

    return (
      (includeActive ? counts.active : 0) +
      (includeDeleted ? counts.deleted : 0)
    )
  }

  private passesDeletedFilters(
    deleted: boolean,
    filters: DeletedFilter[],
  ): boolean {
    if (!filters.length) {
      return true
    }

    return filters.every((filter) =>
      filter.operator === 'eq'
        ? deleted === filter.value
        : deleted !== filter.value,
    )
  }

  private sortRepaymentsByClauses(
    repayments: Repayments,
    clauses: string[],
  ): Repayments {
    if (!clauses.length) {
      return repayments
    }

    const specs = clauses
      .map((clause) => this.parseOrderClause(clause))
      .filter((spec): spec is OrderSpec => !!spec)

    if (!specs.length) {
      return repayments
    }

    const sorted = [...repayments]
    sorted.sort((a, b) => this.compareByOrderSpecs(a, b, specs))
    return sorted
  }

  private parseOrderClause(clause: string): OrderSpec | null {
    if (!clause) {
      return null
    }
    const segments = clause.split('.').filter(Boolean)
    const field = segments.shift()
    if (!field) {
      return null
    }
    let direction: 'asc' | 'desc' = 'asc'
    let nulls: 'first' | 'last' | undefined

    segments.forEach((segment) => {
      if (segment === 'asc' || segment === 'desc') {
        direction = segment
      } else if (segment === 'nullsfirst') {
        nulls = 'first'
      } else if (segment === 'nullslast') {
        nulls = 'last'
      }
    })

    return { field, direction, nulls }
  }

  private compareByOrderSpecs(
    a: Repayment,
    b: Repayment,
    specs: OrderSpec[],
  ): number {
    for (const spec of specs) {
      const aValue = this.resolveOrderValue(a, spec.field)
      const bValue = this.resolveOrderValue(b, spec.field)
      const comparison = this.compareValues(aValue, bValue, spec)
      if (comparison !== 0) {
        return comparison
      }
    }
    return 0
  }

  private resolveOrderValue(record: Repayment, field: string): any {
    if (field === 'embedded_balance_zero_first') {
      const balance = this.parseNumericValue(record.balance)
      return balance === 0 ? 0 : 1
    }

    const normalizedField = field.replace(/\(/g, '.').replace(/\)/g, '')

    if (!normalizedField.includes('.')) {
      return (record as Record<string, any>)?.[normalizedField]
    }

    const [parent, child] = normalizedField.split('.', 2)
    const parentValue = (record as Record<string, any>)?.[parent]
    if (parentValue && typeof parentValue === 'object' && child) {
      return parentValue[child as keyof typeof parentValue]
    }
    return undefined
  }

  private compareValues(aValue: any, bValue: any, spec: OrderSpec): number {
    const aNull = aValue === null || aValue === undefined
    const bNull = bValue === null || bValue === undefined

    if (aNull || bNull) {
      if (aNull && bNull) {
        return 0
      }
      const nullsFirst = spec.nulls === 'first'
      if (aNull) {
        return nullsFirst ? -1 : 1
      }
      return nullsFirst ? 1 : -1
    }

    const aNumber = this.parseNumericValue(aValue)
    const bNumber = this.parseNumericValue(bValue)
    let result: number

    if (aNumber !== null && bNumber !== null) {
      result = aNumber - bNumber
    } else if (this.isParsableDate(aValue) && this.isParsableDate(bValue)) {
      result = Date.parse(aValue) - Date.parse(bValue)
    } else {
      result = String(aValue).localeCompare(String(bValue))
    }

    return spec.direction === 'asc' ? result : -result
  }

  private isParsableDate(value: any): boolean {
    if (typeof value !== 'string') {
      return false
    }
    const timestamp = Date.parse(value)
    return !Number.isNaN(timestamp)
  }

  private parseNumericValue(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null
    }

    if (typeof value === 'number') {
      return Number.isNaN(value) ? null : value
    }

    if (typeof value !== 'string') {
      return null
    }

    const trimmed = value.trim()
    if (!trimmed) {
      return null
    }

    const parenNegative = /^\(.*\)$/.test(trimmed)
    let normalized = trimmed.replace(/[^\d.,-]/g, '')
    if (!normalized) {
      return null
    }

    let trailingNegative = false
    if (
      normalized.endsWith('-') &&
      normalized.indexOf('-', 0) === normalized.length - 1
    ) {
      trailingNegative = true
      normalized = normalized.slice(0, -1)
    }

    normalized = normalized.replace(/,/g, '')
    if (!normalized) {
      return null
    }

    const parsed = Number(normalized)
    if (Number.isNaN(parsed)) {
      return null
    }

    if (parenNegative || trailingNegative) {
      return -Math.abs(parsed)
    }
    return parsed
  }

  private parseTotalFromContentRange(range: string | null): number {
    if (!range) {
      return 0
    }
    const [, totalPart] = range.split('/')
    const total = Number(totalPart)
    return Number.isFinite(total) && total >= 0 ? total : 0
  }

  private collectRelationIds(
    repayments: Repayments,
    key: 'merchant_id' | 'terminal_id' | 'cash_advance_id',
  ): Set<string> {
    const ids = new Set<string>()
    repayments.forEach((repayment) => {
      const value = repayment?.[key]
      if (typeof value === 'string' && value.trim() !== '') {
        ids.add(value)
      }
    })
    return ids
  }

  private async fetchEntityMap(
    endpoint: 'merchants' | 'terminals' | 'cash_advances',
    ids: Set<string>,
    token: string,
    signal?: AbortSignal,
  ): Promise<Map<string, { id: string; name?: string }>> {
    const cache = this.getEntityCache(endpoint)
    const result = new Map<string, { id: string; name?: string }>()

    if (ids.size === 0) {
      return result
    }

    const idList = Array.from(ids)
    idList.forEach((id) => {
      const cached = cache.get(id)
      if (cached) {
        result.set(id, cached)
      }
    })

    const missingIds = idList.filter((id) => !cache.has(id))
    if (missingIds.length === 0) {
      return result
    }

    const chunkSize = 50
    const headers = {
      Authorization: `Bearer ${token}`,
    }

    for (let i = 0; i < missingIds.length; i += chunkSize) {
      const chunk = missingIds.slice(i, i + chunkSize)
      const params = new URLSearchParams()
      params.set('select', 'id,name')
      params.set('id', `in.(${chunk.join(',')})`)

      const response = await this.fetchWithPostgrestAuthRetry(
        `${this.postgrestBaseUrl}/${endpoint}?${params.toString()}`,
        {
          method: 'GET',
          headers,
          signal,
        },
        {
          token,
          context: `loading ${endpoint} relation metadata`,
        },
      )

      if (!response.ok) {
        throw new Error(
          `Failed to load ${endpoint}: ${response.status} ${response.statusText}`,
        )
      }

      const records: Array<{ id?: string; name?: string }> =
        (await response.json()) ?? []

      records.forEach((record) => {
        if (record?.id) {
          const entry = {
            id: record.id,
            name: record.name,
          }
          cache.set(record.id, entry)
          result.set(record.id, entry)
        }
      })
    }

    // Ensure all requested ids are present in the result map (including cached)
    idList.forEach((id) => {
      const entry = cache.get(id)
      if (entry && !result.has(id)) {
        result.set(id, entry)
      }
    })

    return result
  }

  private getEntityCache(
    endpoint: 'merchants' | 'terminals' | 'cash_advances',
  ): Map<string, { id: string; name?: string }> {
    switch (endpoint) {
      case 'merchants':
        return this.merchantCache
      case 'terminals':
        return this.terminalCache
      case 'cash_advances':
        return this.cashAdvanceCache
      default:
        return this.merchantCache
    }
  }

  public isCacheComplete(): boolean {
    return this.hasLoadedAllRepayments
  }

  private logRapidCalls(label: string, thresholdMs = 300) {
    const now = Date.now()
    const last = this.lastCallMap.get(label)
    this.lastCallMap.set(label, now)
    if (last && now - last < thresholdMs) {
      console.warn(`[RepaymentsService] rapid call: ${label}`)
    }
  }

  public getFilteredRepaymentsFromCache(): Repayments {
    return this.tableQueryService.applyToDataset(this.repayments)
  }

  public buildBaseQueryParams(): Record<string, string | undefined> {
    const params: Record<string, string | undefined> = {}

    if (this.country && this.country !== 'all') {
      params['country'] = `eq.${this.country}`
    }

    if (this.sourceFileId) {
      params['source_file'] = `eq.${this.sourceFileId}`
    }

    return params
  }

  private async fetchWithPostgrestAuthRetry(
    url: string,
    init: RequestInit,
    options?: {
      token?: string
      context?: string
    },
  ): Promise<Response> {
    const signal = init.signal
    const context = options?.context ?? 'processing request'

    const token = options?.token
      ? options.token
      : await this.authService.getPostgrestJwtToken(signal)

    let response = await this.fetchWithAuthorization(url, init, token)
    if (response.status !== 401) {
      return response
    }

    let refreshedToken: string
    try {
      refreshedToken = await this.authService.refreshPostgrestJwtToken(signal)
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'unknown refresh error'
      throw new Error(
        `PostgREST secret expired during ${context} and automatic refresh failed: ${message}`,
      )
    }

    response = await this.fetchWithAuthorization(url, init, refreshedToken)
    if (response.status === 401) {
      throw new Error(
        `PostgREST secret expired during ${context}. Retried once with a fresh secret but request is still unauthorized. Please reload the page and try again.`,
      )
    }

    return response
  }

  private fetchWithAuthorization(
    url: string,
    init: RequestInit,
    token: string,
  ): Promise<Response> {
    const headers = new Headers(init.headers ?? {})
    headers.set('Authorization', `Bearer ${token}`)

    return fetch(url, {
      ...init,
      headers,
    })
  }

  private async fetchPagedResults(params: {
    endpoint: string
    headers: Record<string, string>
    filter?: string
    signal?: AbortSignal
    offset?: number
    limit?: number
  }): Promise<any[]> {
    const { endpoint, headers, filter, signal } = params
    const limit = params.limit ?? 100
    const offset = params.offset ?? 0

    const postgrestBaseUrl = this.postgrestBaseUrl.startsWith('/')
      ? new URL(this.postgrestBaseUrl, window.location.origin).toString()
      : this.postgrestBaseUrl
    const url = new URL(`${postgrestBaseUrl.replace(/\/+$/, '')}/${endpoint}`)
    url.searchParams.set('limit', String(limit))
    url.searchParams.set('offset', String(offset))
    url.searchParams.set('order', 'repayment_date.desc,id.asc')
    if (filter) {
      filter.split('&').forEach((pair) => {
        if (!pair) {
          return
        }
        const [key, value] = pair.split('=')
        if (key && value) {
          url.searchParams.append(key, value)
        }
      })
    }

    const response = await this.fetchWithPostgrestAuthRetry(
      url.toString(),
      {
        method: 'GET',
        headers,
        signal,
      },
      {
        context: 'loading paged repayments',
      },
    )
    return response.json()
  }

  private async fetchAllPages(params: {
    endpoint: string
    headers: Record<string, string>
    filter?: string
    signal?: AbortSignal
    limit?: number
  }): Promise<any[]> {
    const limit = params.limit ?? 100
    let offset = 0
    const results: any[] = []

    while (true) {
      const page = await this.fetchPagedResults({
        ...params,
        offset,
        limit,
      })
      if (!Array.isArray(page) || page.length === 0) {
        break
      }
      results.push(...page)
      if (page.length < limit) {
        break
      }
      offset += limit
    }

    return results
  }
}
